package com.islamicmedia.site.components

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.key
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.mutableStateMapOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberUpdatedState
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.islamicmedia.shared.domain.model.KidsAgeBand
import com.islamicmedia.shared.domain.model.KidsAlternative
import com.islamicmedia.shared.domain.model.KidsBlock
import com.islamicmedia.shared.domain.model.KidsResult
import com.islamicmedia.shared.domain.model.KidsScan
import com.islamicmedia.shared.domain.model.KidsScanStatus
import com.islamicmedia.shared.domain.model.KidsVerdict
import com.islamicmedia.shared.domain.model.label
import com.islamicmedia.shared.domain.model.MediaType
import com.islamicmedia.shared.presentation.KidsMessage
import com.islamicmedia.shared.presentation.KidsModeController
import com.islamicmedia.shared.presentation.MediaCheckerViewModel
import com.islamicmedia.shared.presentation.PinExitResult
import com.islamicmedia.shared.presentation.PinResetStep
import com.islamicmedia.shared.util.currentTimeMillis
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import kotlinx.coroutines.withTimeoutOrNull
import com.islamicmedia.shared.presentation.HistoryItem
import com.islamicmedia.shared.presentation.KidsPreviewState
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.disabled
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.display
import org.jetbrains.compose.web.css.height
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.width
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.khronos.webgl.ArrayBuffer
import org.khronos.webgl.Int8Array
import org.jetbrains.compose.web.dom.Video
import org.w3c.dom.CanvasRenderingContext2D
import org.w3c.dom.HTMLCanvasElement
import org.w3c.dom.HTMLImageElement
import org.w3c.dom.HTMLInputElement
import org.w3c.dom.HTMLVideoElement
import org.w3c.dom.url.URL
import org.w3c.files.Blob
import org.w3c.files.BlobPropertyBag
import org.w3c.files.File
import org.w3c.files.FileReader
import kotlin.js.json

/*
 * KIDZONE (Kids Mode) on the web — the same screens as composeApp's KidsModeScreen /
 * KidsSetupScreen, kept visually in step with them. While it's on, Index.kt renders ONLY
 * [KidsModeContent] (it returns before the header, avatar, menu, paywall and dialogs are
 * composed). Everything shown comes from [KidsModeController], which never holds an adult result.
 */

/** Verdict → (bright fill, deep edge). Same values as the app's Kidz palette. */
private fun KidsVerdict.fill() = when (this) {
    KidsVerdict.GO -> "#22C55E"
    KidsVerdict.ASK -> "#FFB300"
    KidsVerdict.NO -> "#FF3B30"
}

private fun KidsVerdict.edge() = when (this) {
    KidsVerdict.GO -> "#15803D"
    KidsVerdict.ASK -> "#B45309"
    KidsVerdict.NO -> "#B91C1C"
}

private val PASTELS = listOf("#FFE1EE", "#D9F0FF", "#FFF4C2", "#EBE2FF")

// Icons: Flaticon "Special Flat" family by Magnific (credited in About Us), in /images/kids/.

/** The icon for a "What's inside" line. The server sends a fixed emoji per concern; this maps it. */
private fun pointIcon(emoji: String): String? = when {
    emoji.startsWith("🙊") -> "pt_language"
    emoji.startsWith("👊") -> "pt_violence"
    emoji.startsWith("🙈") -> "pt_romance"
    emoji.startsWith("👗") -> "pt_immodest"
    emoji.startsWith("🚫") -> "pt_forbidden"
    emoji.startsWith("🍺") -> "pt_substance"
    emoji.startsWith("🎰") -> "pt_gambling"
    emoji.startsWith("🪄") -> "pt_magic"
    emoji.startsWith("😤") -> "pt_disrespect"
    emoji.startsWith("🕌") -> "pt_islam"
    emoji.startsWith("👀") -> "pt_custom"
    emoji.startsWith("⚠") -> "pt_other"
    else -> null
}

private fun mediaTypeIcon(type: MediaType): String = when (type) {
    MediaType.MOVIES -> "mt_movie"
    MediaType.TV_SHOWS -> "mt_tv"
    MediaType.GAMES -> "mt_game"
    MediaType.YOUTUBE -> "mt_youtube"
    MediaType.BOOKS -> "mt_book"
}

@Composable
private fun KidsImg(name: String, sizePx: Int, alt: String = "") {
    Img(src = "/images/kids/$name.png", alt = alt) {
        classes("kids-img")
        style { width(sizePx.px); height(sizePx.px) }
    }
}

/** Every icon KIDZONE shows, the Home tiles first. Mobile bundles them, so they are there on the first frame; the web fetches them early. */
private val KIDS_ICONS = listOf(
    "camera", "keyboard", "lock", "rocket", "sad", "grownup", "retry", "pin", "exit", "kids",
    "tip_party", "tip_good", "tip_islam", "tip_growing",
    "pt_custom", "pt_language", "pt_violence", "pt_romance", "pt_immodest", "pt_forbidden",
    "pt_substance", "pt_gambling", "pt_magic", "pt_disrespect", "pt_islam", "pt_other",
    "mt_movie", "mt_tv", "mt_game", "mt_youtube", "mt_book", "game_runner_pic", "game_flappy_pic"
)
private var kidsIconsRequested = false
private val kidsIconsHeld = mutableListOf<dynamic>()

/** Starts loading the icons now (once), so they are in the browser cache before the screen that shows them composes. */
private fun preloadKidsIcons() {
    if (kidsIconsRequested) return
    kidsIconsRequested = true
    KIDS_ICONS.forEach { name ->
        val img: dynamic = js("new Image()")
        img.src = "/images/kids/$name.png"
        kidsIconsHeld.add(img)
    }
}

/**
 * KIDZONE is a child's screen: nothing third-party may float over it (mobile has no such layer). Hides the
 * WiserNotify "just signed up" popups for as long as it is composed; Index.kt also never installs the
 * pixel while a kids screen is up, so this only matters when it was already on the page.
 */
@Composable
private fun HideSocialProof() {
    DisposableEffect(Unit) {
        val hide = document.createElement("style")
        hide.id = "kids-hide-social-proof"
        hide.textContent = "[id*='wisernotify' i],[class*='wisernotify' i],[id*='wisermapp' i],[class*='wisermapp' i],[id^='wn-'],[class^='wn-'],[class*=' wn-'],[id^='wiser' i],[class^='wiser' i],[class*=' wiser' i],iframe[src*='wisernotify']{display:none !important}"
        document.head?.appendChild(hide)
        onDispose { hide.remove() }
    }
}

@Composable
fun KidsModeContent(viewModel: MediaCheckerViewModel, onExited: () -> Unit = {}) {
    KidsStyles()
    HideSocialProof()
    // Mobile reads the icons and the three verdict animations as soon as Kids Mode composes; the web asks for them now (the logo font too).
    LaunchedEffect(Unit) {
        preloadKidsIcons()
        preloadKidsAssets()
    }
    val kids = viewModel.kids
    val state by kids.state.collectAsState()
    var showExit by remember { mutableStateOf(false) }
    var showCamera by remember { mutableStateOf(false) }
    // The game open over the current check (here, not in KidsScanView, so browser Back closes it first).
    var game by remember { mutableStateOf<String?>(null) }
    LaunchedEffect(state.openScanId) { game = null }
    // Talk cards the kid already uncovered (by scan), so going back and opening the answer again doesn't cover it again.
    val uncoveredTalks = remember { mutableStateMapOf<String, Boolean>() }
    val picking = state.choices.isNotEmpty() || state.photoChoices.isNotEmpty()
    // Each view starts at the top (mobile gives every view its own scroll state): opening or closing a check, or the pick list, never leaves the page scrolled part-way.
    val viewKey = when {
        state.openScanId != null -> "scan:${state.openScanId}"
        picking -> "pick"
        else -> "home"
    }
    LaunchedEffect(viewKey) { window.scrollTo(0.0, 0.0) }
    val fileInputId = remember { "kids-camera-${kotlin.random.Random.nextInt()}" }
    // The photo just taken (an object URL), shown while it's read and looked up.
    var photoUrl by remember { mutableStateOf<String?>(null) }
    val readingPhoto = photoUrl != null && (state.isReadingPhoto || state.isSearching)
    LaunchedEffect(state.isReadingPhoto, state.isSearching) {
        if (!state.isReadingPhoto && !state.isSearching) {
            photoUrl?.let { URL.revokeObjectURL(it) }
            photoUrl = null
        }
    }
    val onPhoto: (ByteArray) -> Unit = { bytes ->
        photoUrl?.let { URL.revokeObjectURL(it) }
        photoUrl = URL.createObjectURL(Blob(arrayOf(bytes.unsafeCast<Int8Array>()), BlobPropertyBag(type = "image/jpeg")))
        kids.onPhoto(bytes)
    }

    // Browser back peels one layer off and never leaves the page (a reload keeps KIDZONE on
    // anyway — the lock is in localStorage). Same order as mobile: the reading-photo screen and the
    // pick list both end in clearChoices(), which cancels the read / search and returns Home. At Home
    // it opens the parent's PIN pad (the only way out), and back from the PIN pad returns Home.
    val onBack by rememberUpdatedState {
        when {
            showExit -> { showExit = false; kids.cancelPinReset() }
            showCamera -> showCamera = false
            game != null -> game = null
            state.openScanId != null -> kids.closeScan()
            picking || readingPhoto -> kids.clearChoices()
            else -> showExit = true
        }
    }
    DisposableEffect(Unit) {
        // One spare entry for Back to consume. Started from setup, that screen's spare is still on top: take it over.
        if (KidsHistory.handOff) KidsHistory.handOff = false
        else window.history.pushState(null, "", window.location.href)
        val handler: (org.w3c.dom.events.Event) -> Unit = {
            window.history.pushState(null, "", window.location.href)
            onBack()
        }
        window.addEventListener("popstate", handler)
        onDispose {
            window.removeEventListener("popstate", handler)
            // Left with the PIN: the spare is still on top. Index.kt adopts it (and pops it if nothing is open).
            if (!viewModel.kids.state.value.enabled) KidsHistory.leftSpare = true
        }
    }
    // Same as the parent app: a returning tab reattaches any check whose stream was dropped.
    DisposableEffect(Unit) {
        val onFocus: (org.w3c.dom.events.Event) -> Unit = { viewModel.onAppForegrounded() }
        window.addEventListener("focus", onFocus)
        onDispose { window.removeEventListener("focus", onFocus) }
    }

    // Fallback only (no camera / camera refused): the browser's own photo picker.
    // capture="environment": phone browsers open the back camera straight away.
    Input(InputType.File) {
        id(fileInputId)
        attr("accept", "image/*")
        attr("capture", "environment")
        style { display(DisplayStyle.None) }
        onChange { readPhoto(fileInputId) { onPhoto(it) } }
    }

    Div({ classes(*listOfNotNull("kids-root", if (state.band == KidsAgeBand.OLDER) "older" else null).toTypedArray()) }) {
        val open = state.openScan
        Div({ classes("kids-wrap", if (open != null) "scan" else if (picking) "pick" else "home") }) {
            when {
                open != null -> KidsScanView(open, state.progress[open.id], state.band, state.alternatives[open.id],
                    onBack = { kids.closeScan() }, onRetry = { kids.retry(open.id) }, onPlay = { game = it },
                    onLoadAlternatives = { kids.loadAlternatives(open.id) }, onCheckAlternative = { kids.checkAlternative(it) },
                    talkUncovered = uncoveredTalks[open.id] == true, onTalkUncovered = { uncoveredTalks[open.id] = true },
                    speech = { kids.speechAudio(it) })
                picking -> KidsPickView(kids)
                else -> KidsHome(kids, onScan = { showCamera = true }, onGrownUps = { showExit = true })
            }
        }
        if (readingPhoto) {
            KidsReadingPhoto(photoUrl, if (state.isReadingPhoto) "Reading your photo…" else "Looking for it…") { kids.clearChoices() }
        }
        if (showCamera) {
            KidsCameraOverlay(
                onPhoto = { bytes -> showCamera = false; onPhoto(bytes) },
                onClose = { showCamera = false },
                onUnavailable = {
                    showCamera = false
                    (document.getElementById(fileInputId) as? HTMLInputElement)?.click()
                }
            )
        }
        // The game floats over the check, which stays visible (greyed) behind it — the rocket bar
        // keeps moving, so the kid can see their check is still going.
        val playing = game
        if (playing != null && state.openScan != null) GameModal(playing, answerReady = state.openScan?.result != null) { game = null }
        state.block?.let { AskGrownUp(it) { kids.dismissBlock() } }
        if (showExit) {
            KidsExitSheet(
                kids,
                onCancel = { showExit = false; kids.cancelPinReset() },
                onExited = {
                    showExit = false
                    viewModel.onKidsModeExited()
                    onExited()
                }
            )
        }
    }
}

// ── KIDZONE title ───────────────────────────────────────────────────────────

/**
 * One letter of the logo — the numbers are mobile's `TitleLetters` (KidsComponents.kt), copied as they are.
 * [tilt] (deg) and [bounce] (fraction of the font size, + is down) are its resting pose; [rock] is the degrees
 * either side of the tilt it rocks, [hop] the fraction of the font size it rises, [squish] how much it
 * stretches at the top of a hop, [rockMs] / [hopMs] the one-way times and [startMs] the hold before it starts.
 * [advUnits] is the glyph's advance in jumpman.ttf (2048 units per em), which sizes its layout box.
 */
private class TitleLetter(
    val char: String,
    val color: String,
    val tilt: Int,
    val bounce: Double,
    val rock: Int,
    val hop: Double,
    val squish: Double,
    val rockMs: Int,
    val hopMs: Int,
    val startMs: Int,
    val advUnits: Int,
) {
    val adv: Double get() = advUnits / 2048.0
}

private val TITLE_LETTERS = listOf(
    TitleLetter("K", "#049CD8", -8, 0.02, 4, 0.06, 0.05, 820, 610, 0, 900),
    TitleLetter("I", "#E52521", 6, -0.06, 5, 0.08, 0.07, 1040, 700, 180, 350),
    TitleLetter("D", "#43B047", -4, 0.04, 3, 0.05, 0.04, 700, 560, 360, 900),
    TitleLetter("Z", "#049CD8", 7, -0.03, 4, 0.07, 0.05, 930, 650, 90, 900),
    TitleLetter("O", "#FBD000", -6, 0.05, 6, 0.06, 0.08, 1180, 760, 270, 1200),
    TitleLetter("N", "#E52521", 5, -0.05, 3, 0.07, 0.05, 780, 590, 450, 900),
    TitleLetter("E", "#43B047", -7, 0.02, 4, 0.06, 0.06, 990, 680, 130, 900),
)

/** The animated logo as a menu row (the menu is outside KIDZONE, so it brings its own styles). */
@Composable
fun KidzoneMenuLabel() {
    KidsStyles()
    // The menu is where KIDZONE is opened from: start fetching its icons now, so they are cached by the time it opens.
    // Mobile reads the icons and the three verdict animations as soon as Kids Mode composes; the web asks for them now (the logo font too).
    LaunchedEffect(Unit) {
        preloadKidsIcons()
        preloadKidsAssets()
    }
    KidzoneTitle(26)
}

/** A box's width as laid out: fractional, and untouched by transforms (what mobile's scaleToFillWidth measures). */
private fun layoutWidthPx(el: org.w3c.dom.Element): Double =
    window.getComputedStyle(el).getPropertyValue("width").removeSuffix("px").toDoubleOrNull() ?: 0.0

private fun fontSizePx(el: org.w3c.dom.Element): Double =
    window.getComputedStyle(el).getPropertyValue("font-size").removeSuffix("px").toDoubleOrNull() ?: 0.0

private fun round3(v: Double): Double = kotlin.math.round(v * 1000.0) / 1000.0

/**
 * Fits what is inside a `.kids-fit` box, from the box's width:
 *  - the logo (`.kidzone-title.fill`): its font-size is set so its natural layout width is exactly the box less
 *    6px a side (mobile: 6dp padding, then scaleToFillWidth). Its width is linear in its font-size, so one
 *    measurement is enough;
 *  - the question (`.kids-h1.fit`): one line, the biggest size from 30px down to 16px (steps of 0.5) at which
 *    it really fits — mobile's TextAutoSize.StepBased, measured on the font actually in use.
 */
private fun fitKidsBox(box: org.w3c.dom.HTMLElement) {
    val boxWidth = layoutWidthPx(box)
    if (boxWidth <= 0.0) return
    (box.querySelector(".kidzone-title.fill") as? org.w3c.dom.HTMLElement)?.let { title ->
        val target = boxWidth - 12.0
        // Twice: the browser lays the letter boxes out in 1/64px steps, so the second pass closes the last hair.
        repeat(2) {
            val natural = layoutWidthPx(title)
            val size = fontSizePx(title)
            if (natural > 0.0 && size > 0.0 && target > 0.0) title.style.fontSize = "${round3(size * target / natural)}px"
        }
    }
    (box.querySelector(".kids-h1.fit") as? org.w3c.dom.HTMLElement)?.let { line ->
        fun widthAt(px: Double): Double {
            line.style.fontSize = "${px}px"
            return layoutWidthPx(line)
        }
        var size = 30.0
        val natural = widthAt(size)
        if (natural > boxWidth) {
            size = (kotlin.math.floor(30.0 * boxWidth / natural * 2.0) / 2.0).coerceIn(16.0, 30.0)
            while (size > 16.0 && widthAt(size) > boxWidth) size -= 0.5
            while (size + 0.5 <= 30.0 && widthAt(size + 0.5) <= boxWidth) size += 0.5
        }
        line.style.fontSize = "${size}px"
    }
}

/**
 * Keeps a `.kids-fit` box fitted ([fitKidsBox]): now, whenever its width changes (rotating the phone, a
 * scrollbar appearing) and once the fonts are in. It watches the box's zero-height `.kids-fit-probe`
 * child rather than the box itself — fitting changes the box's height, and an observer that sees its own
 * target change size in its callback raises the "ResizeObserver loop" error. Returns the cleanup.
 */
private fun watchKidsFit(box: org.w3c.dom.HTMLElement): () -> Unit {
    var disposed = false
    var fittedWidth = -1.0
    val fit: (Boolean) -> Unit = { force ->
        if (!disposed) {
            val width = layoutWidthPx(box)
            if (force || width != fittedWidth) {
                fittedWidth = width
                fitKidsBox(box)
            }
        }
    }
    fit(true)
    val fonts: dynamic = document.asDynamic().fonts
    if (fonts != null && fonts != undefined) fonts.ready.then({ fit(true) })
    val observerClass = window.asDynamic().ResizeObserver
    var observer: dynamic = null
    val resizeHandler: (org.w3c.dom.events.Event) -> Unit = { fit(false) }
    if (observerClass != null && observerClass != undefined) {
        observer = js("Reflect").construct(observerClass, arrayOf<dynamic>({ _: dynamic -> fit(false) }))
        observer.observe(box.querySelector(".kids-fit-probe") ?: box)
    } else {
        window.addEventListener("resize", resizeHandler)
    }
    return {
        disposed = true
        if (observer != null) observer.disconnect() else window.removeEventListener("resize", resizeHandler)
    }
}

/**
 * [fill]: scaled to exactly the width of the nearest `.kids-fit` box (like mobile's fillWidth), by [fitKidsBox]; [sizePx] is then ignored.
 *
 * Each letter is two spans, like mobile's two graphicsLayers: the outer one is the letter's box (its advance
 * scaled 1.35, like TextGeometricTransform) and rocks / hops around its middle; the inner one holds the
 * glyph (scaleX 1.35) and squishes from its feet. The numbers reach the CSS as per-letter variables.
 */
@Composable
private fun KidzoneTitle(sizePx: Int, fill: Boolean = false, still: Boolean = false) {
    Div({
        classes("kidzone-title")
        // 8 and over: the letters keep their resting pose (mobile's animated = false).
        if (still) classes("still")
        if (fill) classes("fill") else style { property("font-size", "${sizePx}px") }
    }) {
        TITLE_LETTERS.forEach { l ->
            Span({
                style {
                    property("color", l.color)
                    property("--adv", "${l.adv}")
                    property("--tilt", "${l.tilt}deg")
                    property("--bounce", "${l.bounce}")
                    property("--rock", "${l.rock}deg")
                    property("--hop", "${l.hop}")
                    property("--squish", "${l.squish}")
                    property("--rock-ms", "${l.rockMs}ms")
                    property("--hop-ms", "${l.hopMs}ms")
                    property("--start", "${l.startMs}ms")
                }
            }) { Span({ classes("g") }) { Text(l.char) } }
        }
    }
}

/** "KIDZ" for the history tag: the logo's first four letters, same colours, outline, tilt and spacing, standing still.
 *  [sizePx] is mobile's fontSize: 13 for the history tag (the default), 14 on the "See what your child saw" button.
 *  Brings its own font + styles, because the kids stylesheet only exists while Kids Mode is open. */
@Composable
fun KidzTag(sizePx: Int = 13) {
    InjectStyles("kidz-tag-styles", """
@font-face{font-family:'Jumpman';src:url('/fonts/jumpman.ttf') format('truetype');font-display:swap}
.kidz-tag{display:inline-flex;font-family:'Jumpman','Arial Black',Impact,sans-serif;font-weight:400;font-size:13px;line-height:1.1;filter:drop-shadow(0 .07em 0 #000);user-select:none}
.kidz-tag span{display:flex;justify-content:center;flex:none;width:calc(var(--adv) * 1.35em);-webkit-text-stroke:.11em #000;paint-order:stroke fill}
.kidz-tag span+span{margin-left:-.07em}
.kidz-tag span:nth-child(2){position:relative;z-index:1}
""")
    Div({ classes("kidz-tag"); style { property("font-size", "${sizePx}px") } }) {
        TITLE_LETTERS.take(4).forEach { l ->
            Span({
                style {
                    property("color", l.color)
                    property("--adv", "${l.adv}")
                    property("transform", "translateY(${l.bounce}em) rotate(${l.tilt}deg) scaleX(1.35)")
                }
            }) { Text(l.char) }
        }
    }
}

// ── Parent side: "what did my child see?" ──────────────────────────────────

/** The button in the parent's detail view of a Kids Mode check. */
@Composable
fun KidsPreviewButton(verdict: KidsVerdict?, onClick: () -> Unit) {
    KidsStyles()  // the flip animation classes live here; the kids stylesheet isn't otherwise loaded outside Kids Mode
    Div({
        classes("kids-preview-btn")
        onClick { onClick() }
    }) {
        KidzTag(14)
        Span { Text("See what your child saw") }
    }
}

/**
 * One card, two faces. Drives the flip of the detail card: [cardClass] is the CSS class to put on the card
 * (null at rest, so an unflipped card carries no transform at all) and [showBack] says which face is up.
 *
 * Mobile turns the card ONE way, 0 to 180 degrees over 600ms (FastOutSlowIn), and flipping back runs the same
 * turn in reverse. The web does the same: the card's transform is transitioned between rotateY(0) and
 * rotateY(180deg) (`kidsflip-to0` / `kidsflip-to180`), and the face is swapped when the card's real angle (read
 * back from its computed matrix, every frame) passes 90 degrees, like mobile's `showBack = angle > 90`.
 * A flip that is reversed half-way turns back from where it is, as mobile's Animatable does.
 */
class KidsFlip internal constructor() {
    var cardClass: String? by mutableStateOf(null)
        internal set
    var showBack: Boolean by mutableStateOf(false)
        internal set
}

private const val KIDS_FLIP_CLASSES = ".kidsflip-s0,.kidsflip-s180,.kidsflip-to0,.kidsflip-to180"

/** The card's turn about the vertical axis in degrees (0 = front, 180 = back), from its computed matrix; NaN while it has no 3D transform. */
private fun kidsFlipAngle(card: org.w3c.dom.Element): Double {
    val t = window.getComputedStyle(card).getPropertyValue("transform")
    if (!t.startsWith("matrix3d(")) return Double.NaN
    val v = t.removePrefix("matrix3d(").removeSuffix(")").split(",").map { it.trim().toDoubleOrNull() ?: 0.0 }
    if (v.size < 16) return Double.NaN
    // perspective(d) rotateY(a): the first column is (cos a, 0, -sin a, ...). abs() keeps 180 from reading as -180.
    return kotlin.math.atan2(kotlin.math.abs(v[2]), v[0]) * 180.0 / kotlin.math.PI
}

@Composable
fun rememberKidsFlip(flipped: Boolean): KidsFlip {
    val flip = remember { KidsFlip() }
    LaunchedEffect(flipped) {
        val target = if (flipped) 180.0 else 0.0
        if (flip.cardClass == null) {
            if (flipped == flip.showBack) return@LaunchedEffect
            // At rest the card carries no transform. Put it on the angle it turns from (no transition on this class),
            // and give the browser a couple of frames to lay that out, so the next class has something to turn from.
            flip.cardClass = if (flipped) "kidsflip-s0" else "kidsflip-s180"
            delay(40)
        }
        flip.cardClass = if (flipped) "kidsflip-to180" else "kidsflip-to0"
        val t0 = window.performance.now()
        while (window.performance.now() - t0 < 1000.0) {
            delay(16)
            val card = document.querySelector(KIDS_FLIP_CLASSES) ?: break
            val angle = kidsFlipAngle(card)
            if (angle.isNaN()) continue
            val past = angle > 90.0
            if (past != flip.showBack) flip.showBack = past
            if (kotlin.math.abs(angle - target) < 0.05) break
        }
        flip.showBack = flipped
        flip.cardClass = null
    }
    return flip
}

/** The back face: the child's own screen, in the kids' look. Fills the card. */
@Composable
fun KidsPreviewBackFace(
    item: HistoryItem,
    state: KidsPreviewState,
    onRetry: () -> Unit,
    onFlipBack: () -> Unit,
    /** The age band KIDZONE is set up for on this device, so the parent sees exactly the child's screen. */
    band: KidsAgeBand = KidsAgeBand.YOUNG
) {
    val m = Regex("""^(.*\S)\s*\((\d{4})\)\s*$""").find(item.title)
    val title = m?.groupValues?.get(1) ?: item.title
    val year = m?.groupValues?.get(2)
    Div({ classes(*listOfNotNull("kidsflip-back-face", "kids-root", if (band == KidsAgeBand.OLDER) "older" else null).toTypedArray()) }) {
        Div({ classes("kidsflip-bar") }) {
            Button({ classes("kidsflip-back"); onClick { onFlipBack() } }) { Text("Back to analysis") }
            Span({ classes("kidsflip-label") }) { Text("What your child saw") }
        }
        Div({ classes("kidsflip-body") }) {
            when (state) {
                KidsPreviewState.Loading -> Div({ classes("kids-spin", "big", "kidsflip-wait") })
                KidsPreviewState.Failed -> {
                    Div({ classes("kidsflip-fail") }) { Text("Couldn't load what your child saw.") }
                    Button({ classes("kidsflip-retry"); onClick { onRetry() } }) { Text("Try again") }
                }
                is KidsPreviewState.Ready -> {
                    val scan = KidsScan(
                        id = item.id, title = title, mediaType = item.mediaType, year = year,
                        coverUrl = state.result.coverUrl ?: item.result.coverUrl,
                        status = KidsScanStatus.DONE, result = state.result
                    )
                    KidsResultView(scan, state.result, band)
                }
            }
        }
    }
}

// ── Verdict sign ────────────────────────────────────────────────────────────

/** What KIDZONE needs from the network before any check finishes, started when it opens: the logo font. */
private fun preloadKidsAssets() {
    try {
        val loading: dynamic = document.asDynamic().fonts.load("40px Jumpman")
        loading.catch({ _: dynamic -> })
    } catch (_: Throwable) {}
}

/**
 * The verdict as the traffic light every kid knows, and nothing else (no word): lit RED (no), AMBER (maybe)
 * or GREEN (yes), on a white disc. Plain HTML/CSS, nothing to load. Mirrors the app's VerdictSign.
 */
@Composable
private fun VerdictSign(verdict: KidsVerdict, sizePx: Int) {
    Div({
        classes("kids-sign")
        attr("role", "img")
        attr("aria-label", verdict.word)
        style { width(sizePx.px); height(sizePx.px); property("font-size", "${sizePx}px") }
    }) {
        Div({ classes("light") }) {
            Span({ classes("red"); if (verdict == KidsVerdict.NO) classes("on") })
            Span({ classes("amber"); if (verdict == KidsVerdict.ASK) classes("on") })
            Span({ classes("green"); if (verdict == KidsVerdict.GO) classes("on") })
        }
    }
}

// ── Home ────────────────────────────────────────────────────────────────────

@Composable
private fun KidsHome(kids: KidsModeController, onScan: () -> Unit, onGrownUps: () -> Unit) {
    val state by kids.state.collectAsState()
    val nameInputId = remember { "kids-name-${kotlin.random.Random.nextInt()}" }

    // The way out, top-left like any back button — grown-ups only, it opens the PIN pad.
    Button({ classes("kids-parents"); onClick { onGrownUps() } }) { Text("← Parents") }
    val older = state.band == KidsAgeBand.OLDER
    // The logo, dancing, as wide as the screen allows; the question always on one line (both sized by watchKidsFit).
    // 8 and over: the logo standing still at a modest size, and a plainer question.
    Div({
        classes("kids-fit")
        ref { box ->
            val stop = watchKidsFit(box)
            onDispose { stop() }
        }
    }) {
        Div({ classes("kids-fit-probe") })
        if (older) Div({ classes("kids-center") }) { KidzoneTitle(40, still = true) }
        else KidzoneTitle(0, fill = true)
        Div({ classes("kids-h1", "fit") }) { Text(if (older) "What do you want to check?" else "What are we checking today?") }
    }

    // Two big choices, side by side: show it (camera) or tell it (type the name).
    val busy = state.isReadingPhoto || state.isSearching
    var typing by remember { mutableStateOf(state.query.isNotBlank()) }
    // The name box slides in only when the child opens it; Home opening with a name already typed shows it in place (mobile's AnimatedVisibility starts visible).
    var slideIn by remember { mutableStateOf(false) }
    // Mobile waits for the box to land (220ms), then focuses it — also when Home opens with a name already typed.
    // A tap on "I'll tell you" focuses it at once (below) because phone browsers only raise the keyboard for that.
    LaunchedEffect(typing) {
        if (typing) {
            delay(220)
            (document.getElementById(nameInputId) as? HTMLInputElement)?.focus()
        }
    }
    val openTyping = {
        if (!typing) slideIn = true   // already open (e.g. back from the pick list): don't replay the slide
        typing = true
        // Focus inside the tap itself: phone browsers only raise the keyboard for a focus the tap caused.
        (document.getElementById(nameInputId) as? HTMLInputElement)?.focus()
    }
    if (older) {
        // 8 and over: the same two choices, level and in quieter colours.
        Div({ classes("kids-tiles", "calm") }) {
            KidsActionTile("camera", "Scan the cover", tilt = 0,
                face = "linear-gradient(to bottom right,#3B5BDB,#4C6EF5)", edge = "#2B44A8", enabled = !busy, onClick = onScan)
            KidsActionTile("keyboard", "Type the name", tilt = 0,
                face = "linear-gradient(to bottom right,#334155,#475569)", edge = "#1E293B", enabled = !busy) { openTyping() }
        }
    } else {
        // 7 and under: most can't type a title yet, so the camera is THE way in, full width.
        // Typing is a small link under it, for the grown-up helping or a confident reader.
        Div({ classes("kids-tiles", "single") }) {
            KidsActionTile("camera", "I'll show you", tilt = -3,
                face = "linear-gradient(to bottom right,#FF8A00,#FF3D7F)", edge = "#B0124F", enabled = !busy, onClick = onScan)
        }
        if (!typing) {
            Button({ classes("kids-typelink"); if (busy) disabled(); onClick { openTyping() } }) { Text("Type a name instead") }
        }
    }

    // The name box slides down into view when "I'll tell you" is tapped. Always in the page (so the
    // tap can focus it), folded to nothing while closed.
    Div({
        classes("kids-typebox")
        if (typing) {
            classes("open")
            if (slideIn) classes("slide")
        } else attr("aria-hidden", "true")
    }) {
        Div({ classes("inner") }) {
            Div({ classes("kids-namerow") }) {
                Div({ classes("kids-namefield") }) {
                    Input(InputType.Text) {
                        id(nameInputId)
                        classes("kids-input")
                        if (!typing) attr("tabindex", "-1")
                        value(state.query)
                        attr("placeholder", "Type a name")
                        attr("enterkeyhint", "search")
                        attr("autocomplete", "off")
                        onInput { kids.onQueryChange(it.value) }
                        onKeyDown {
                            if (it.key == "Enter") {
                                (document.getElementById(nameInputId) as? HTMLInputElement)?.blur()
                                kids.search()
                            }
                        }
                    }
                    Button({
                        classes("kids-clear")
                        attr("aria-label", "Clear")
                        if (!typing) attr("tabindex", "-1")
                        onClick {
                            kids.onQueryChange("")
                            (document.getElementById(nameInputId) as? HTMLInputElement)?.blur()
                            typing = false
                        }
                    }) { Text("✕") }
                }
                Button({
                    classes("kids-go")
                    if (!typing) attr("tabindex", "-1")
                    if (state.query.isBlank() || state.isSearching) disabled()
                    onClick {
                        (document.getElementById(nameInputId) as? HTMLInputElement)?.blur()
                        kids.search()
                    }
                }) { Text("Go") }
            }
        }
    }
    // Known titles matching what's typed, like the main search bar: a tap checks it straight away.
    // Words only, never covers (the list is every title anyone has checked). Mirrors mobile's KidsSuggestions.
    if (typing && !busy && state.suggestions.isNotEmpty()) {
        Div({ classes("kids-suggest") }) {
            state.suggestions.forEach { item ->
                val type = KidsModeController.parseMediaType(item.mediaType)
                Button({
                    classes("row")
                    onClick {
                        (document.getElementById(nameInputId) as? HTMLInputElement)?.blur()
                        kids.pickSuggestion(item)
                    }
                }) {
                    KidsImg(mediaTypeIcon(type), 34)
                    Div({ classes("txt") }) {
                        Div({ classes("t") }) { Text(item.title) }
                        Div({ classes("d") }) { Text(listOfNotNull(type.singularLabel, item.year?.takeIf { it.isNotBlank() }).joinToString(" · ")) }
                    }
                    Span({ classes("chev") }) { Text("›") }
                }
            }
        }
    }
    // A photo gets the big KidsReadingPhoto screen instead; this row is for a typed name.
    if (state.isSearching || state.isReadingPhoto) {
        Div({ classes("kids-busy") }) {
            Div({ classes("kids-spin") })
            Text(if (state.isReadingPhoto) "Reading your photo…" else "Looking for it…")
        }
    }
    state.message?.let { msg ->
        Div({ classes("kids-bubble", "row"); onClick { kids.dismissMessage() } }) { KidsImg(kidsMessageIcon(msg), 44); Span { Text(kidsMessageText(msg)) } }
    }
    if (state.scans.isNotEmpty()) {
        Div({ classes("kids-h2") }) { Text("My checks") }
        Div({ classes("kids-list") }) {
            state.scans.forEach { scan ->
                key(scan.id) { ScanRow(scan, state.band) { kids.openScan(scan.id) } }
            }
        }
    }
}

/**
 * A big square "candy" tile: icon on top, label under it, pressing down into a darker edge. It
 * leans by [tilt] and keeps still (only the logo moves); the CSS is `.kids-tile` in [KidsStyles].
 */
@Composable
private fun KidsActionTile(
    icon: String,
    label: String,
    tilt: Int,
    face: String,
    edge: String,
    enabled: Boolean,
    onClick: () -> Unit,
) {
    Button({
        classes("kids-tile")
        style {
            property("--tilt", "${tilt}deg")
            property("--face", face)
            property("--edge", edge)
        }
        if (!enabled) disabled()
        onClick { onClick() }
    }) {
        Span({ classes("face") }) {
            KidsImg(icon, 76)
            Span { Text(label) }
        }
    }
}

@Composable
private fun ScanRow(scan: KidsScan, band: KidsAgeBand, onClick: () -> Unit) {
    val verdict = scan.result?.verdictFor(band)
    Button({ classes("kids-row"); onClick { onClick() } }) {
        Div({
            classes("frame")
            style { property("background", verdict?.fill() ?: "#FFFFFF") }
        }) {
            KidsCover(scan.result?.coverUrl ?: scan.coverUrl, scan.mediaType, 56, 80, 11)
        }
        Div({ classes("info") }) {
            Div({ classes("name") }) { Text(scan.title) }
            Div({ classes("meta") }) {
                Text(listOfNotNull(scan.mediaType.singularLabel, scan.year?.takeIf { it.isNotBlank() }).joinToString(" · "))
            }
        }
        Div({ classes("end") }) {
            when (scan.status) {
                KidsScanStatus.DONE -> verdict?.let { VerdictSign(it, 48) }
                KidsScanStatus.FAILED -> Div({ classes("kids-disc") }) { KidsImg("sad", 30) }
                KidsScanStatus.BLOCKED -> Div({ classes("kids-disc") }) { KidsImg("grownup", 30) }
                KidsScanStatus.CHECKING -> Div({ classes("kids-spinbox") }) { Div({ classes("kids-spin", "s30") }) }
            }
        }
    }
}

// ── Pick one ────────────────────────────────────────────────────────────────

private val ACCENTS = listOf("#049CD8", "#FF3D7F", "#43B047", "#FF8A00")

@Composable
private fun KidsPickView(kids: KidsModeController) {
    val state by kids.state.collectAsState()
    Button({ classes("kids-pill", "back"); onClick { kids.clearChoices() } }) { Text("‹ Back") }
    Div({ classes("kids-h1") }) { Text("Which one is it?") }
    state.choices.forEachIndexed { i, item ->
        val type = KidsModeController.parseMediaType(item.mediaType)
        ChoiceCard(item.title, listOfNotNull("${type.emoji} ${type.singularLabel}", item.year).joinToString(" · "), ACCENTS[i % ACCENTS.size],
            art = { KidsCover(item.coverUrl, type, 64, 90, 10) }) { kids.pick(item) }
    }
    state.photoChoices.forEachIndexed { i, candidate ->
        val type = candidate.mediaType
        ChoiceCard(candidate.title, type?.let { "${it.emoji} ${it.singularLabel}" } ?: "", ACCENTS[i % ACCENTS.size],
            art = { Div({ classes("kids-choice-icon") }) { KidsImg(type?.let { mediaTypeIcon(it) } ?: "pt_custom", 56) } }) { kids.pickPhotoChoice(candidate) }
    }
    // Mobile's Spacer(8): a real box, because an 8px margin would just merge into the 12px under the last card.
    Div({ style { height(8.px) } })
    Button({ classes("kids-btn", "secondary"); onClick { kids.clearChoices() } }) { Text("None of these") }
}

@Composable
private fun ChoiceCard(title: String, detail: String, accent: String, art: @Composable () -> Unit, onClick: () -> Unit) {
    // The accent colours the 10px strip down the left edge and the arrow circle (CSS var --accent).
    Button({ classes("kids-choice"); style { property("--accent", accent) }; onClick { onClick() } }) {
        art()
        Div({ classes("txt") }) {
            Div({ classes("t") }) { Text(title) }
            if (detail.isNotBlank()) Div({ classes("d") }) { Text(detail) }
        }
        Span({ classes("chev") }) { Text("›") }
    }
}

// ── One scan: waiting / result ──────────────────────────────────────────────

@Composable
private fun KidsScanView(
    scan: KidsScan,
    progress: Float?,
    band: KidsAgeBand,
    alternatives: List<KidsAlternative>?,
    onBack: () -> Unit,
    onRetry: () -> Unit,
    onPlay: (String) -> Unit,
    onLoadAlternatives: () -> Unit,
    onCheckAlternative: (KidsAlternative) -> Unit,
    talkUncovered: Boolean,
    onTalkUncovered: () -> Unit,
    speech: suspend (String) -> ByteArray?,
) {
    Button({ classes("kids-pill", "back"); onClick { onBack() } }) { Text("‹ Back") }
    Div({ classes("kids-center") }) {
        val result = scan.result
        when {
            scan.status == KidsScanStatus.DONE && result != null -> KidsResultView(scan, result, band, onSearchAnother = onBack,
                alternatives = alternatives, onLoadAlternatives = onLoadAlternatives, onCheckAlternative = onCheckAlternative,
                talkUncovered = talkUncovered, onTalkUncovered = onTalkUncovered, speech = speech)
            scan.status == KidsScanStatus.CHECKING -> {
                Div({ classes("kids-cover-frame") }) { KidsCover(scan.coverUrl, scan.mediaType, 140, 196, 15) }
                Div({ classes("kids-h1", "small", "checking") }) { Text("Checking ${scan.title}…") }
                RocketProgress(progress ?: 0f)
                Div({ classes("kids-wait-sub") }) { Text("This can take a minute or two.") }
                Div({ classes("kids-label", "center", "play") }) { Text("Play while you wait") }
                Div({ classes("kids-games") }) {
                    GameCard("game_runner_pic", "Runner", "#FF8A00") { onPlay("runner") }
                    GameCard("game_flappy_pic", "Flappy", "#049CD8") { onPlay("flappy") }
                    if (band == KidsAgeBand.OLDER) {
                        // Full width under the two, its picture drawn in CSS (mobile's TriviaArt).
                        Button({ classes("kids-game", "wide"); onClick { onPlay("trivia") } }) {
                            Div({ classes("trivia-art") }) {
                                Span({ classes("disc") }) { Text("?") }
                                Span({ classes("moon") })
                                Span({ classes("disc", "ex") }) { Text("!") }
                            }
                            Div({ classes("row") }) {
                                Span({ classes("name") }) { Text("Islamic Trivia") }
                                Span({ classes("play"); style { property("background", "#0D9488") } }) { Text("▶") }
                            }
                        }
                    }
                }
            }
            scan.status == KidsScanStatus.BLOCKED -> {
                BigNote("grownup", "Ask a grown-up to help with this one.")
            }
            else -> {
                BigNote("sad", "Oops! We couldn't check this one.")
                Button({ classes("kids-btn", "retry"); onClick { onRetry() } }) { KidsImg("retry", 44); Text("Try again") }
            }
        }
    }
}

@Composable
internal fun KidsResultView(
    scan: KidsScan,
    result: KidsResult,
    band: KidsAgeBand,
    /** The kid's own view: a "Search another title" button at the end. Null in the parent's preview. */
    onSearchAnother: (() -> Unit)? = null,
    /** 8 and over, on a NO: "Try one of these instead" (null = not looked up yet). The preview passes none. */
    alternatives: List<KidsAlternative>? = null,
    onLoadAlternatives: (() -> Unit)? = null,
    onCheckAlternative: ((KidsAlternative) -> Unit)? = null,
    /** The talk card starts uncovered (the kid popped its bubbles before). */
    talkUncovered: Boolean = false,
    onTalkUncovered: () -> Unit = {},
    /** 7 and under: "Read it to me" in the KIDZONE voice (null = no clip). The preview passes none: browser voice. */
    speech: (suspend (String) -> ByteArray?)? = null,
) {
    val older = band == KidsAgeBand.OLDER
    val verdict = result.verdictFor(band)
    if (older && verdict == KidsVerdict.NO && onLoadAlternatives != null) LaunchedEffect(scan.id) { onLoadAlternatives() }
    Div({ classes("kids-cover-frame", "result") }) { KidsCover(result.coverUrl ?: scan.coverUrl, scan.mediaType, 112, 158, 14) }
    Div({ classes("kids-title") }) { Text(scan.title) }
    // The traffic light on the left and GO / SLOW DOWN / STOP beside it (the sign already tells screen readers).
    Div({
        classes(*listOfNotNull("kids-verdict", if (older) "older" else null).toTypedArray())
        style {
            property("background", verdict.fill())
            property("box-shadow", "0 6px 0 ${verdict.edge()}")
            property("--edge", verdict.edge())
        }
    }) {
        VerdictSign(verdict, if (older) 84 else 96)
        Div({ classes("w"); attr("aria-hidden", "true") }) { Text(verdict.word) }
    }
    if (!older) {
        // 7 and under: many can't read yet, so the answer is read out loud: in the KIDZONE voice when the
        // server has a clip (asked for as the answer opens, so a tap usually plays at once), else the browser's own.
        // The "talk to a grown-up" line only once its card is uncovered: the voice mustn't give it away.
        val script = result.spokenText(band, talk = talkUncovered).let { if (result.title.isBlank()) "${scan.title}. $it" else it }
        var clip by remember(script) { mutableStateOf<ByteArray?>(null) }
        var waiting by remember(script) { mutableStateOf(false) }
        val scope = rememberCoroutineScope()
        if (speech != null) LaunchedEffect(script) { clip = speech(script) }
        DisposableEffect(scan.id) { onDispose { stopKidsSpeech() } }
        Button({ classes("kids-btn", "retry", "speak"); onClick {
            if (waiting) return@onClick
            stopKidsSpeech()
            val ready = clip
            when {
                ready != null -> playKidsAudio(ready) { speakKids(script) }
                speech == null -> speakKids(script)
                else -> {
                    unlockKidsAudio()   // still inside the tap, so the clip may play when it arrives (Safari)
                    scope.launch {
                        waiting = true
                        val audio = withTimeoutOrNull(SPEECH_TAP_WAIT_MS) { speech(script) }
                        waiting = false
                        if (audio != null) { clip = audio; playKidsAudio(audio) { speakKids(script) } } else speakKids(script)
                    }
                }
            }
        } }) { Span({ classes("kids-emoji") }) { Text(if (waiting) "⏳" else "🔊") }; Text("Read it to me") }
        // OpenAI's terms: say the voice is AI-made.
        if (speech != null) Div({ classes("kids-ai-voice") }) { Text("AI voice") }
    }
    val points = result.pointsFor(band)
    if (points.isNotEmpty()) {
        Div({ classes("kids-label") }) { Text("What's inside") }
        points.forEachIndexed { i, point ->
            Div({
                classes(*listOfNotNull("kids-card", "point", if (older && point.detail != null) "detail" else null).toTypedArray())
                if (!older) style { property("background", PASTELS[i % PASTELS.size]) }
            }) {
                Span({ classes("e") }) { pointIcon(point.emoji)?.let { KidsImg(it, if (older) 30 else 36) } ?: Text(point.emoji) }
                Div {
                    Div({ classes("x") }) { Text(point.label(band)) }
                    if (older) point.detail?.let { Div({ classes("d") }) { Text(it) } }
                }
            }
        }
    } else if (verdict == KidsVerdict.GO) {
        TipCard("tip_party", null, "Nothing to worry about!", "#DCFCE7", "#15803D")
    }
    Div({ style { height(8.px) } })
    val positives = if (older) result.positives.orEmpty() else emptyList()
    if (positives.isNotEmpty()) {
        // 8 and over: the good things about this title, like the grown-ups' "Positive aspects".
        Div({ classes("kids-label", "positives") }) { Text("Positive aspects") }
        Div({ classes("kids-positives") }) {
            positives.forEach { line -> Div({ classes("l") }) { Span({ classes("tick") }); Span { Text(line) } } }
        }
    } else {
        result.goodStuff?.let { TipCard("tip_good", "Good stuff", it, "#FFF1B8", "#B45309") }
    }
    // 8 and over: what Islam teaches, explained for this title (else the approved library line).
    if (older) TipCard("tip_islam", "Islam teaches", result.islamAbout ?: result.islamicTip, "#DDF4EE", "#0F766E")
    if (verdict != KidsVerdict.GO) {
        // 8 and over, on a MAYBE: what to talk about. Otherwise (and from an older server) the general line.
        val talk = result.talkAbout?.takeIf { older && verdict == KidsVerdict.ASK } ?: "Talk to a grown up about this one."
        // The kid's mission, so it stands out from the pastel tips, hidden under bubbles they tap away (mobile's TalkCard).
        key(scan.id) {
            var uncovered by remember { mutableStateOf(talkUncovered) }
            Div({ classes("kids-talk") }) {
                Div({ classes("x"); if (!uncovered) attr("aria-hidden", "true") }) { Text(talk) }
                if (!uncovered) BubbleCover { uncovered = true; onTalkUncovered() }
            }
        }
    }
    if (older && verdict == KidsVerdict.NO && !alternatives.isNullOrEmpty() && onCheckAlternative != null) {
        Div({ classes("kids-label", "alts") }) { Text("Try one of these instead") }
        Div({ classes("kids-alts") }) {
            alternatives.forEach { alt ->
                Button({ classes("kids-alt"); onClick { onCheckAlternative(alt) } }) {
                    Div({ classes("kids-cover-frame", "alt") }) {
                        KidsCover(alt.coverUrl, KidsModeController.parseMediaType(alt.mediaType), 70, 100, 10)
                    }
                    Div({ classes("n") }) { Text(alt.title) }
                }
            }
        }
    }
    onSearchAnother?.let { go ->
        Div({ style { height(10.px) } })
        Button({ classes("kids-btn"); onClick { go() } }) { KidsImg("camera", 44); Text("Search another title") }
    }
    Div({ style { height(16.px) } })
}

/** A tap on "Read it to me" waits this long for a clip still being made, then the browser's own voice reads it. */
private const val SPEECH_TAP_WAIT_MS = 8_000L

/** Plays the KIDZONE voice. One element for every clip, so a tap can unlock it for a clip that arrives later. */
private val kidsVoice: dynamic by lazy { js("new Audio()") }
private var kidsVoiceUrl: String? = null

/** Plays a WAV clip in the KIDZONE voice; [onFail] runs instead if the browser won't play it. */
private fun playKidsAudio(audio: ByteArray, onFail: () -> Unit) {
    try {
        kidsVoiceUrl?.let { URL.revokeObjectURL(it) }
        val url = URL.createObjectURL(Blob(arrayOf(audio.unsafeCast<Int8Array>()), BlobPropertyBag(type = "audio/wav")))
        kidsVoiceUrl = url
        kidsVoice.src = url
        kidsVoice.play().then(null) { _: dynamic -> onFail() }
    } catch (_: Throwable) { onFail() }
}

/**
 * Safari only lets an audio element play from a tap, so a clip that arrives after the tap would be silent. Playing a
 * moment of silence during the tap unlocks the element for it.
 */
private fun unlockKidsAudio() {
    try {
        val silence = ByteArray(44 + 1_600).also { wav ->
            fun int(at: Int, v: Int) { for (i in 0..3) wav[at + i] = (v shr (8 * i)).toByte() }
            "RIFF".forEachIndexed { i, c -> wav[i] = c.code.toByte() }; int(4, 36 + 1_600)
            "WAVEfmt ".forEachIndexed { i, c -> wav[8 + i] = c.code.toByte() }; int(16, 16)
            int(20, 1 or (1 shl 16)); int(24, 8_000); int(28, 16_000); int(32, 2 or (16 shl 16))
            "data".forEachIndexed { i, c -> wav[36 + i] = c.code.toByte() }; int(40, 1_600)
        }
        playKidsAudio(silence) {}
    } catch (_: Throwable) {}
}

/** "Read it to me" (7 and under) when there is no clip: the browser's own text-to-speech. Silent where it isn't supported. */
private fun speakKids(text: String) {
    try {
        val synth = window.asDynamic().speechSynthesis ?: return
        synth.cancel()
        val utterance: dynamic = js("new SpeechSynthesisUtterance()")
        utterance.text = text
        utterance.rate = 0.9   // a little slower, for small listeners
        synth.speak(utterance)
    } catch (_: Throwable) {}
}

private fun stopKidsSpeech() {
    try { window.asDynamic().speechSynthesis?.cancel() } catch (_: Throwable) {}
    try { kidsVoice.pause() } catch (_: Throwable) {}
}

@Composable
private fun TipCard(icon: String, label: String?, text: String, bg: String, labelColor: String) {
    Div({ classes("kids-card"); style { property("background", bg) } }) {
        Span({ classes("e") }) { KidsImg(icon, 36) }
        Div {
            if (label != null) Div({ classes("l"); style { property("color", labelColor) } }) { Text(label) }
            Div({ classes("x") }) { Text(text) }
        }
    }
}

private class Bubble(val x: Double, val y: Double, val r: Double, val color: String)

private val BUBBLE_COLORS = listOf("#FFD6E8", "#D6ECFF", "#FFF1B8", "#E4D9FF", "#D5F5E3", "#FFE0C7")

/**
 * Glossy pastel bubbles packed over what they hide, with a "Tap to uncover" label (mobile's BubbleCover). Each
 * tap pops the few bubbles nearest the finger; once most are gone the rest pop and [onUncovered] runs.
 * Fills the nearest positioned parent.
 */
@Composable
private fun BubbleCover(label: String = "Tap to uncover", onUncovered: () -> Unit) {
    var box by remember { mutableStateOf<org.w3c.dom.HTMLElement?>(null) }
    var area by remember { mutableStateOf<Pair<Double, Double>?>(null) }
    var popped by remember { mutableStateOf(emptySet<Int>()) }
    val bubbles = remember(area) { area?.let { (w, h) -> packBubbles(w, h, 34.0) }.orEmpty() }
    LaunchedEffect(popped.size, bubbles) {
        if (bubbles.isNotEmpty() && popped.size >= bubbles.size) { delay(260); onUncovered() }
    }
    fun popAll() { popped = bubbles.indices.toSet() }
    fun popNear(x: Double, y: Double) {
        val left = bubbles.indices.filter { it !in popped }
            .sortedBy { (bubbles[it].x - x).let { dx -> dx * dx } + (bubbles[it].y - y).let { dy -> dy * dy } }
        if (left.isEmpty()) return
        try { window.navigator.asDynamic().vibrate(8) } catch (_: Throwable) {}
        // The bubble under the finger and up to three neighbours.
        val near = left.take(4).filter { i -> kotlin.math.hypot(bubbles[i].x - x, bubbles[i].y - y) < 68.0 }.ifEmpty { left.take(1) }
        popped = popped + near
        if (popped.size >= bubbles.size * 0.8) popAll()
    }
    Div({
        classes("kids-bubbles")
        attr("role", "button")
        attr("tabindex", "0")
        attr("aria-label", label)
        ref { el ->
            box = el
            val measure = { area = el.clientWidth.toDouble() to el.clientHeight.toDouble() }
            measure()
            val onResize: (org.w3c.dom.events.Event) -> Unit = { measure() }
            window.addEventListener("resize", onResize)
            onDispose { box = null; window.removeEventListener("resize", onResize) }
        }
        onClick { e ->
            val rect = box?.getBoundingClientRect() ?: return@onClick
            // A keyboard "click" has no position: uncover it all.
            if (e.nativeEvent.asDynamic().detail == 0) popAll() else popNear(e.clientX - rect.left, e.clientY - rect.top)
        }
    }) {
        bubbles.forEachIndexed { i, b ->
            Span({
                classes(*listOfNotNull("b", if (i in popped) "pop" else null).toTypedArray())
                style {
                    property("left", "${b.x - b.r}px"); property("top", "${b.y - b.r}px")
                    width((b.r * 2).px); height((b.r * 2).px); property("--c", b.color)
                }
            })
        }
        if (bubbles.isNotEmpty() && popped.size < bubbles.size * 0.6) Span({ classes("lbl") }) { Text("👆 $label") }
    }
}

/** A hex grid that leaves no gap (mobile's packBubbles): neighbours 1.6 radii apart, sizes varied a little. */
private fun packBubbles(width: Double, height: Double, radius: Double): List<Bubble> {
    if (width <= 0 || height <= 0) return emptyList()
    val random = kotlin.random.Random(7)
    val dx = radius * 1.6
    val dy = dx * 0.866
    val rows = kotlin.math.ceil(height / dy).toInt() + 1
    val cols = kotlin.math.ceil(width / dx).toInt() + 1
    return buildList {
        for (row in 0 until rows) for (col in 0 until cols) {
            val x = col * dx + if (row % 2 == 1) dx / 2 else 0.0
            val y = row * dy
            if (x - radius > width || y - radius > height) continue
            add(Bubble(x, y, radius * (1 + random.nextDouble() * 0.15), BUBBLE_COLORS[(row * 2 + col) % BUBBLE_COLORS.size]))
        }
    }
}

/** A game in a card over the (greyed) check. Taps outside the card do nothing — kids mash the screen mid-game. */
@Composable
private fun GameModal(game: String, answerReady: Boolean, onClose: () -> Unit) {
    Div({ classes("kids-game-modal") }) {
        Div({ classes("card") }) {
            if (answerReady) {
                Div({ classes("ready"); onClick { onClose() } }) { KidsImg("tip_party", 34); Text("Your answer is ready! Tap to see it") }
            }
            Div({ classes("head") }) {
                Span({ classes("name") }) { Text(when (game) { "runner" -> "Runner"; "flappy" -> "Flappy"; else -> "Islamic Trivia" }) }
                Button({ classes("x"); attr("aria-label", "Close game"); onClick { onClose() } }) { Text("✕") }
            }
            Div({ classes("kids-game-box") }) {
                when (game) {
                    "runner" -> FitrahRunnerGame()
                    "flappy" -> FlappyCrescentGame()
                    "trivia" -> IslamicTriviaGame()
                }
            }
        }
    }
}

/** A game button: a real picture of the game, its name, and a big ▶. Mirrors the app's GameCard. */
@Composable
private fun GameCard(picture: String, label: String, accent: String, onClick: () -> Unit) {
    Button({ classes("kids-game"); onClick { onClick() } }) {
        Img(src = "/images/kids/$picture.png", alt = "") { classes("pic") }
        Div({ classes("row") }) {
            Span({ classes("name") }) { Text(label) }
            Span({ classes("play"); style { property("background", accent) } }) { Text("▶") }
        }
    }
}

/**
 * "Checking…" bar: an orange candy-stripe track with a rocket flying along its tip. One warm colour
 * on purpose (it used to be a rainbow). Creeps a little past the last real step so it never freezes.
 */
@Composable
private fun RocketProgress(target: Float) {
    var creep by remember { mutableStateOf(0f) }
    LaunchedEffect(target) {
        creep = 0f
        while (creep < 0.1f) { delay(1_000); creep += 0.004f }
    }
    val shown = ((target + creep).coerceIn(0.1f, 0.96f) * 100)
    Div({ classes("kids-rocketbar") }) {
        Div({ classes("track") }) { Div({ classes("fill"); style { width(shown.percent) } }) }
        Img(src = "/images/kids/rocket.png", alt = "") { classes("rocket"); style { property("left", "clamp(0px, calc(${shown}% - 11.5px), calc(100% - 46px))") } }
    }
}

/**
 * KIDZONE's own camera (same as the app's KidsCameraScreen): live back camera, a box to fit the
 * cover in, one big button. [onUnavailable] when the browser has no camera or it's refused — the
 * caller falls back to the browser's photo picker.
 */
@Composable
private fun KidsCameraOverlay(onPhoto: (ByteArray) -> Unit, onClose: () -> Unit, onUnavailable: () -> Unit) {
    var video by remember { mutableStateOf<HTMLVideoElement?>(null) }
    var stream by remember { mutableStateOf<dynamic>(null) }
    var busy by remember { mutableStateOf(false) }
    var flash by remember { mutableStateOf(false) }
    val unavailable by rememberUpdatedState(onUnavailable)

    DisposableEffect(Unit) {
        var active = true
        var opened: dynamic = null
        val media = window.navigator.asDynamic().mediaDevices
        if (media == null || media.getUserMedia == undefined) {
            unavailable()
        } else {
            val constraints = json(
                "audio" to false,
                "video" to json("facingMode" to json("ideal" to "environment"), "width" to json("ideal" to 1920), "height" to json("ideal" to 1080))
            )
            media.getUserMedia(constraints).then({ s: dynamic ->
                if (active) { opened = s; stream = s } else stopTracks(s)
            }, { _: dynamic -> if (active) unavailable() })
        }
        onDispose {
            active = false
            if (opened != null) stopTracks(opened)
        }
    }
    LaunchedEffect(video, stream) {
        val v = video
        if (v != null && stream != null) {
            v.asDynamic().srcObject = stream
            v.play()
        }
    }

    Div({ classes("kids-cam") }) {
        Video({
            attr("playsinline", "")
            attr("autoplay", "")
            attr("muted", "")
            ref { el -> el.muted = true; video = el; onDispose { video = null } }
        })
        Div({ classes("box") }) {
            Div({ classes("hint") }) { Text("Fit the cover in the box") }
        }
        Button({ classes("close"); attr("aria-label", "Close"); onClick { onClose() } }) { Text("✕") }
        Button({
            classes("shutter")
            attr("aria-label", "Take the picture")
            if (busy || stream == null) disabled()
            onClick {
                val v = video ?: return@onClick
                busy = true
                flash = true
                window.setTimeout({ flash = false }, 350)
                snapJpeg(v) { bytes ->
                    busy = false
                    if (bytes != null && bytes.isNotEmpty()) onPhoto(bytes)
                }
            }
        }) { if (busy) Div({ classes("kids-spin", "big", "white") }) else KidsImg("camera", 48) }
        if (flash) Div({ classes("flash") })
    }
}

/**
 * While a photo is being read (and looked up): the kid's own photo in a tilted snapshot frame, a
 * magnifying glass looping over it, a scan line, popping sparkles and hopping letters. Mirrors the
 * app's KidsReadingPhoto.
 */
@Composable
private fun KidsReadingPhoto(photoUrl: String?, line: String, onBack: () -> Unit) {
    Div({ classes("kids-reading") }) {
        Button({ classes("kids-pill", "back"); onClick { onBack() } }) { Text("‹ Back") }
        Div({ classes("stage") }) {
            Div({ classes("snap") }) {
                Div({ classes("pic") }) {
                    if (photoUrl != null) Img(src = photoUrl, alt = "") else KidsImg("camera", 96)
                    Div({ classes("beam") })
                }
            }
            // Five four-pointed stars (a clipped square, filled with the colour), each popping on its own beat.
            listOf("#FBD000" to "s1", "#FF3D7F" to "s2", "#049CD8" to "s3", "#43B047" to "s4", "#FF8A00" to "s5").forEach { (c, k) ->
                Span({ classes("spark", k); style { property("background", c) } })
            }
            Div({ classes("glass") })
        }
        Div({ classes("line") }) {
            line.forEachIndexed { i, ch ->
                Span({ style { property("animation-delay", "${i * 100}ms") } }) { Text(if (ch == ' ') "\u00A0" else ch.toString()) }
            }
        }
    }
}

/**
 * Mobile's magnifier path (KidsReadingPhoto): over one 2.6s loop (t = 0..2π) it moves to (62 sin t, 48 sin 2t - 10),
 * turns 8 cos t degrees and breathes 1 + .06 sin 2t. Written out as 32 linear steps, which is smooth to under a pixel.
 */
private fun glassKeyframes(): String {
    val steps = 32
    return (0..steps).joinToString("") { i ->
        val t = 2.0 * kotlin.math.PI * i / steps
        val x = round3(62.0 * kotlin.math.sin(t))
        val y = round3(48.0 * kotlin.math.sin(2.0 * t) - 10.0)
        val turn = round3(8.0 * kotlin.math.cos(t))
        val scale = round3(1.0 + 0.06 * kotlin.math.sin(2.0 * t))
        "${round3(100.0 * i / steps)}%{transform:translate(${x}px,${y}px) rotate(${turn}deg) scale($scale)}"
    }
}

/**
 * Mobile's magnifying glass, drawn the way KidsReadingPhoto draws it on its 120px canvas (r 36, centre 48,48): the
 * orange handle first (under the lens), then the lens (white at .28) with a purple ring of 10.08, then the white
 * highlight arc (radius 22.32, 200..260 degrees, 5.04 wide, round ends).
 */
private fun glassBackground(): String {
    val encode: dynamic = js("encodeURIComponent")
    val svg = "<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 120 120'>" +
        "<line x1='74.64' y1='74.64' x2='111' y2='111' stroke='#FF8A00' stroke-width='15.12' stroke-linecap='round'/>" +
        "<circle cx='48' cy='48' r='36' fill='#FFFFFF' fill-opacity='.28'/>" +
        "<circle cx='48' cy='48' r='36' fill='none' stroke='#8B5CF6' stroke-width='10.08'/>" +
        "<path d='M27.03 40.37A22.32 22.32 0 0 1 44.12 26.02' fill='none' stroke='#FFFFFF' stroke-opacity='.9' stroke-width='5.04' stroke-linecap='round'/>" +
        "</svg>"
    return "url(\"data:image/svg+xml," + (encode(svg) as String) + "\")"
}

private val GLASS_KEYFRAMES = glassKeyframes()
private val GLASS_BACKGROUND = glassBackground()

private fun stopTracks(stream: dynamic) {
    try {
        val tracks = stream.getTracks()
        val n = (tracks.length as Int)
        for (i in 0 until n) tracks[i].stop()
    } catch (_: Throwable) {}
}

/** The long edge of every photo KIDZONE sends: about 2,000px, like the app (a full-size phone photo made the upload the slow part). */
private const val KIDS_PHOTO_MAX_EDGE = 2048.0

/** The current video frame as a JPEG, at most 2048px on the long edge (same cap as the app). */
private fun snapJpeg(video: HTMLVideoElement, onBytes: (ByteArray?) -> Unit) = encodeJpeg(video, video.videoWidth, video.videoHeight, onBytes)

/** [source] (a video frame or a decoded photo, [w] x [h]) drawn at most [KIDS_PHOTO_MAX_EDGE] px on the long edge and encoded as a JPEG (quality .85). */
private fun encodeJpeg(source: dynamic, w: Int, h: Int, onBytes: (ByteArray?) -> Unit) {
    if (w <= 0 || h <= 0) { onBytes(null); return }
    val scale = minOf(1.0, KIDS_PHOTO_MAX_EDGE / maxOf(w, h))
    val canvas = document.createElement("canvas") as HTMLCanvasElement
    canvas.width = maxOf(1, (w * scale).toInt())
    canvas.height = maxOf(1, (h * scale).toInt())
    val context = canvas.getContext("2d") as CanvasRenderingContext2D
    // A JPEG has no see-through: a transparent PNG would turn black.
    context.fillStyle = "#fff"
    context.fillRect(0.0, 0.0, canvas.width.toDouble(), canvas.height.toDouble())
    context.drawImage(source, 0.0, 0.0, canvas.width.toDouble(), canvas.height.toDouble())
    canvas.toBlob({ blob -> blobBytes(blob, onBytes) }, "image/jpeg", 0.85)
}

private fun blobBytes(blob: Blob?, onBytes: (ByteArray?) -> Unit) {
    if (blob == null) { onBytes(null); return }
    val reader = FileReader()
    reader.onload = {
        val buffer = reader.result as? ArrayBuffer
        onBytes(buffer?.let { b -> val int8 = Int8Array(b); ByteArray(int8.length) { i -> int8.asDynamic()[i] as Byte } })
    }
    reader.onerror = { onBytes(null) }
    reader.readAsArrayBuffer(blob)
}

@Composable
private fun BigNote(icon: String, text: String) {
    Div({ style { property("margin-top", "30px") } }) { KidsImg(icon, 110) }
    Div({ classes("kids-h1", "small", "note") }) { Text(text) }
}

/** Where a cover's picture is: still coming, in, or failed to load. */
private enum class CoverLoad { LOADING, LOADED, FAILED }

/**
 * Mobile's loading spinner for a cover (CoverLoadingSpinner): Material's indeterminate arc, 2dp stroke, the
 * theme's primary teal, [size] wide. Drawn as an SVG background whose stroke stays 2px at any size; the arc
 * grows and shrinks inside the SVG (same proportions as the other .kids-spin arcs) while the element turns.
 */
private fun coverSpinnerBackground(size: Double): String {
    fun n(v: Double): String = (kotlin.math.round(v * 100.0) / 100.0).toString()
    val encode: dynamic = js("encodeURIComponent")
    val box = n(size)
    val r = (size - 2.0) / 2.0
    val c = 2.0 * kotlin.math.PI * r
    val gap = n(c * 2.0)
    val svg = "<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 $box $box'>" +
        "<circle cx='${n(size / 2.0)}' cy='${n(size / 2.0)}' r='${n(r)}' fill='none' stroke='#0E7C7B' stroke-width='2' stroke-dasharray='${n(c * 0.71)} $gap'>" +
        "<animate attributeName='stroke-dasharray' values='${n(c * 0.0124)} $gap;${n(c * 0.71)} $gap;${n(c * 0.71)} $gap' keyTimes='0;.5;1' calcMode='spline' keySplines='.42 0 .58 1;.42 0 .58 1' dur='1.33s' repeatCount='indefinite'/>" +
        "<animate attributeName='stroke-dashoffset' values='0;${n(-c * 0.278)};${n(-c * 0.987)}' keyTimes='0;.5;1' calcMode='spline' keySplines='.42 0 .58 1;.42 0 .58 1' dur='1.33s' repeatCount='indefinite'/>" +
        "</circle></svg>"
    return "url(\"data:image/svg+xml," + (encode(svg) as String) + "\")"
}

/**
 * A title's cover — mobile's KidsArtwork / MediaArtwork: a [w] x [h] tile with corners of [radius] px, tinted with the
 * media type's colour (14%). While the picture loads it shows a spinner (0.55 x the width); if the picture fails,
 * the media type's emoji (0.42 x the width); with no URL at all, the media type's icon (0.62 x the width).
 */
@Composable
private fun KidsCover(url: String?, mediaType: MediaType, w: Int, h: Int, radius: Int) {
    val tint = "#" + mediaType.activeColorHex.toString(16).takeLast(6) + "24"
    val tile: org.jetbrains.compose.web.attributes.AttrsScope<org.w3c.dom.HTMLDivElement>.() -> Unit = {
        classes("kids-cover")
        style {
            width(w.px); height(h.px)
            property("border-radius", "${radius}px")
            property("background", tint)
        }
    }
    if (url.isNullOrBlank()) {
        Div(tile) { KidsImg(mediaTypeIcon(mediaType), (w * 0.62).toInt()) }
        return
    }
    key(url) {
        var load by remember { mutableStateOf(CoverLoad.LOADING) }
        Div(tile) {
            if (load != CoverLoad.FAILED) {
                Img(src = url, alt = "") {
                    classes("cv")
                    if (load == CoverLoad.LOADED) classes("on")
                    ref { img ->
                        val onLoaded: (org.w3c.dom.events.Event) -> Unit = { load = CoverLoad.LOADED }
                        val onFailed: (org.w3c.dom.events.Event) -> Unit = { load = CoverLoad.FAILED }
                        img.addEventListener("load", onLoaded)
                        img.addEventListener("error", onFailed)
                        // Already finished (cached) before the listeners were on.
                        if (img.complete) load = if (img.naturalWidth > 0) CoverLoad.LOADED else CoverLoad.FAILED
                        onDispose {
                            img.removeEventListener("load", onLoaded)
                            img.removeEventListener("error", onFailed)
                        }
                    }
                }
            }
            when (load) {
                CoverLoad.LOADING -> {
                    val side = w * 0.55
                    Div({
                        classes("spin")
                        style {
                            width(side.px); height(side.px)
                            property("background-image", coverSpinnerBackground(side))
                        }
                    })
                }
                CoverLoad.FAILED -> Span({ classes("glyph"); style { property("font-size", "${w * 0.42}px") } }) { Text(mediaType.emoji) }
                CoverLoad.LOADED -> {}
            }
        }
    }
}

private fun kidsMessageText(message: KidsMessage): String = when (message) {
    KidsMessage.NOT_FOUND -> "Hmm, we couldn't find that. Try typing it another way!"
    KidsMessage.PHOTO_UNREADABLE -> "We couldn't read that photo. Try getting closer to the cover!"
    KidsMessage.OOPS -> "Oops! Something went wrong. Try again."
}

private fun kidsMessageIcon(message: KidsMessage): String = when (message) {
    KidsMessage.NOT_FOUND -> "pt_custom"
    KidsMessage.PHOTO_UNREADABLE -> "camera"
    KidsMessage.OOPS -> "sad"
}

private fun readPhoto(inputId: String, onBytes: (ByteArray) -> Unit) {
    val input = document.getElementById(inputId) as? HTMLInputElement
    val file = input?.files?.item(0)
    if (file != null) uprightJpeg(file, onBytes)
    input?.value = ""
}

/**
 * A photo from the browser's picker, sent the way the camera's are (and the app's): turned the right way up
 * (the EXIF rotation), at most 2048px on the long edge, as a JPEG. A raw phone photo is several MB, can lie on
 * its side, and may be a HEIC. If the browser can't draw it at all, the file goes as it is.
 */
private fun uprightJpeg(file: File, onBytes: (ByteArray) -> Unit) {
    fun original() = blobBytes(file) { bytes -> if (bytes != null) onBytes(bytes) }
    fun encode(source: dynamic, w: Int, h: Int, release: () -> Unit) =
        encodeJpeg(source, w, h) { jpeg -> release(); if (jpeg != null) onBytes(jpeg) else original() }
    // An <img> applies the EXIF rotation itself.
    fun viaImage() {
        val url = URL.createObjectURL(file)
        val img = document.createElement("img") as HTMLImageElement
        img.addEventListener("load", { encode(img, img.naturalWidth, img.naturalHeight) { URL.revokeObjectURL(url) } })
        img.addEventListener("error", { URL.revokeObjectURL(url); original() })
        img.src = url
    }
    val createBitmap = window.asDynamic().createImageBitmap
    if (createBitmap == undefined) { viaImage(); return }
    try {
        createBitmap.call(window, file, json("imageOrientation" to "from-image")).then(
            { bitmap: dynamic -> encode(bitmap, (bitmap.width as Number).toInt(), (bitmap.height as Number).toInt()) { bitmap.close() } },
            { _: dynamic -> viaImage() }
        )
    } catch (_: Throwable) {
        viaImage()
    }
}

/** For the parent/tester, under the kid's line. Only a dev build on the PROD backend sees it. Mobile's wording, word for word. */
private const val SERVER_WITHOUT_KIDS_NOTE =
    "Test build: this phone is on the PROD server, which doesn't have KIDZONE yet. Tap ← Parents, switch to DEV, and sign in there."

@Composable
private fun AskGrownUp(block: KidsBlock, onOk: () -> Unit) {
    Div({ classes("kids-scrim"); onClick { onOk() } }) {
        Div({ classes("kids-modal") }) {
            Div({ classes("kids-modal-head") }) { KidsImg("grownup", 76) }
            Div({ classes("kids-center", "kids-modal-body") }) {
                Div({ classes("kids-modal-title") }) { Text("Ask a grown-up") }
                Div({ classes("kids-modal-msg") }) {
                    Text(when (block) {
                        KidsBlock.GROWN_UP_NEEDED, KidsBlock.SERVER_WITHOUT_KIDS -> "A grown-up needs to help with this one."
                        KidsBlock.DAILY_LIMIT -> "You did lots of checks today! Ask a grown-up."
                    })
                }
                if (block == KidsBlock.SERVER_WITHOUT_KIDS) {
                    Div({ classes("kids-modal-note") }) { Text(SERVER_WITHOUT_KIDS_NOTE) }
                }
                Button({ classes("kids-btn", "purple"); onClick { onOk() } }) { Text("OK") }
            }
        }
    }
}

// ── PIN pad + leaving KIDZONE (grown-ups) ───────────────────────────────────

@Composable
private fun PinPad(title: String, subtitle: String?, error: String?, errorKey: Int, enabled: Boolean = true, onComplete: (String) -> Unit) {
    var digits by remember { mutableStateOf("") }
    LaunchedEffect(errorKey) { if (errorKey > 0) digits = "" }
    Div({ classes("kids-center") }) {
        KidsImg("lock", 56)
        Div({ classes("kids-pin-title") }) { Text(title) }
        if (subtitle != null) Div({ classes("kids-pin-sub") }) { Text(subtitle) }
        // Re-keyed on every error so the shake animation replays.
        key(errorKey) {
            Div({ classes(*listOfNotNull("kids-pin-dots", if (error != null) "err" else null, if (errorKey > 0) "shake" else null).toTypedArray()) }) {
                repeat(4) { i -> Span({ if (i < digits.length) classes("on") }) }
            }
        }
        Div({ classes("kids-pin-err") }) { Text(error ?: "") }
        Div({ classes("kids-keys") }) {
            listOf("1", "2", "3", "4", "5", "6", "7", "8", "9", "", "0", "⌫").forEach { k ->
                Button({
                    classes(*listOfNotNull("kids-key", if (k.isEmpty()) "blank" else null).toTypedArray())
                    if (!enabled || k.isEmpty()) disabled()
                    onClick {
                        if (k == "⌫") digits = digits.dropLast(1)
                        else if (digits.length < 4) {
                            digits += k
                            if (digits.length == 4) {
                                val pin = digits
                                digits = ""
                                onComplete(pin)
                            }
                        }
                    }
                }) { Text(k) }
            }
        }
    }
}

@Composable
private fun NewPinEntry(onPin: (String) -> Unit) {
    var first by remember { mutableStateOf<String?>(null) }
    var error by remember { mutableStateOf<String?>(null) }
    var errorKey by remember { mutableStateOf(0) }
    PinPad(
        title = if (first == null) "Choose a 4-digit PIN" else "Type it again",
        subtitle = if (first == null) "You'll need it to leave KIDZONE" else "Just to be sure",
        error = error,
        errorKey = errorKey
    ) { pin ->
        val chosen = first
        when {
            chosen == null -> { first = pin; error = null }
            chosen == pin -> onPin(pin)
            else -> { first = null; error = "Those didn't match. Let's start again."; errorKey++ }
        }
    }
}

@Composable
private fun KidsExitSheet(kids: KidsModeController, onCancel: () -> Unit, onExited: () -> Unit) {
    val state by kids.state.collectAsState()
    // However the sheet closes (Cancel, browser Back, a successful exit), a half-done PIN reset ends
    // with it. Otherwise a verified reset outlives the sheet, and the next "Parents" tap opens straight
    // into "Choose a 4-digit PIN" — for whoever is holding the device.
    DisposableEffect(Unit) { onDispose { kids.cancelPinReset() } }
    var error by remember { mutableStateOf<String?>(null) }
    var errorKey by remember { mutableStateOf(0) }
    var now by remember { mutableStateOf(currentTimeMillis()) }
    LaunchedEffect(state.pinLockedUntil) {
        while (state.pinLockedUntil > currentTimeMillis()) {
            now = currentTimeMillis()
            delay(1000)
        }
        now = currentTimeMillis()
    }
    val locked = state.pinLockedUntil > now

    Div({ classes("kids-sheet") }) {
        Div({ classes("kids-wrap") }) {
            Button({ classes("kids-link", "s17"); onClick { onCancel() } }) { Text("Cancel") }
            Div({ style { height(12.px) } })
            when (state.resetStep) {
                PinResetStep.IDLE -> {
                    val lockText = if (locked) {
                        val s = ((state.pinLockedUntil - now) / 1000).coerceAtLeast(0)
                        "Too many tries. Try again in ${s / 60}:${(s % 60).toString().padStart(2, '0')}"
                    } else null
                    PinPad("Grown-ups only", "Enter your PIN to leave KIDZONE", lockText ?: error, errorKey, enabled = !locked) { pin ->
                        when (val r = kids.tryExit(pin)) {
                            PinExitResult.Ok -> onExited()
                            is PinExitResult.Wrong -> {
                                error = "Wrong PIN. ${r.attemptsLeft} ${if (r.attemptsLeft == 1) "try" else "tries"} left."
                                errorKey++
                            }
                            is PinExitResult.Locked -> { error = null; errorKey++ }
                        }
                    }
                    Div({ classes("kids-center", "kids-forgot") }) {
                        Button({ classes("kids-link"); onClick { kids.requestPinReset() } }) { Text("Forgot PIN?") }
                    }
                }
                PinResetStep.SENDING -> Div({ classes("kids-center", "kids-pin-wait") }) {
                    Div({ classes("kids-spin", "big", "purple") })
                    Div({ classes("kids-pin-send") }) { Text("Sending a code to your email…") }
                }
                PinResetStep.FAILED -> Div({ classes("kids-center") }) {
                    Div({ classes("kids-pin-fail") }) { Text("We couldn't send the code.") }
                    Div({ classes("kids-pin-sub", "hint") }) { Text("Check your internet connection and try again.") }
                    Div({ style { height(20.px) } })
                    Button({ classes("kids-btn", "purple"); onClick { kids.requestPinReset() } }) { Text("Try again") }
                }
                PinResetStep.CODE_SENT, PinResetStep.VERIFYING -> ResetCodeEntry(kids, state.resetStep == PinResetStep.VERIFYING)
                PinResetStep.VERIFIED -> NewPinEntry { pin -> if (kids.setPinAfterReset(pin)) onExited() }
            }
        }
    }
}

@Composable
private fun ResetCodeEntry(kids: KidsModeController, verifying: Boolean) {
    val state by kids.state.collectAsState()
    var code by remember { mutableStateOf("") }
    Div({ classes("kids-center") }) {
        Div({ style { property("font-size", "44px"); property("line-height", "1.15") } }) { Text("📧") }
        Div({ classes("kids-pin-title") }) { Text("Check your email") }
        Div({ classes("kids-pin-sub", "hint") }) { Text("We sent a 6-digit code to the email on this account.") }
        Div({ style { height(20.px) } })
        Input(InputType.Text) {
            classes("kids-code")
            value(code)
            attr("inputmode", "numeric")
            attr("autocomplete", "one-time-code")
            onInput { code = it.value.filter { c -> c.isDigit() }.take(6); if (state.message != null) kids.dismissMessage() }
            onKeyDown { if (it.key == "Enter" && code.length == 6) kids.verifyPinReset(code) }
        }
        if (state.message == KidsMessage.OOPS) Div({ classes("kids-code-err") }) { Text("That code didn't work. Check it and try again.") }
        Div({ style { height(20.px) } })
        Button({
            classes("kids-btn", "purple")
            if (code.length != 6 || verifying) disabled()
            onClick { kids.verifyPinReset(code) }
        }) { Text(if (verifying) "Checking…" else "Check code") }
        Button({ classes("kids-link", "s14", "up8"); onClick { kids.requestPinReset() } }) { Text("Send a new code") }
    }
}

// ── Parent side: setup ──────────────────────────────────────────────────────

private class SetupBackState {
    var spare = true       // the spare history entry (the avatar menu's, see [KidsHistory]) is still the top one
    var ignoreNext = false // the next popstate is the echo of our own history.back()
}

/**
 * The ONE spare history entry Back consumes while a kids screen is up, passed along instead of piling up.
 * Setup opens from the avatar menu, whose spare Index.kt pushed and never popped (it returns before its
 * history effects while setup is up), so setup reuses it; Start KIDZONE hands it to [KidsModeContent]; a
 * PIN exit returns to setup, which takes it back. Setup closing any other way leaves it for Index.kt, which
 * adopts it as its own layer entry and pops it if no layer is open. So menu → setup → KIDZONE → exit →
 * setup → back leaves history as it found it.
 */
internal object KidsHistory {
    /** Setup closed into KIDZONE with its spare unused: KIDZONE takes it over instead of pushing another. */
    var handOff = false
    /** A kids screen closed with its spare still on top: Index.kt adopts it. */
    var leftSpare = false
}

/**
 * Browser Back on the setup screen. Index.kt's page-level Back handling is not composed while this screen
 * is up (it returns first), so the screen keeps ONE spare history entry for Back to consume, like
 * [KidsModeContent] does: Back steps out of PIN creation, then closes setup. Returns the action for the
 * on-screen "‹ Back", which does the same and also drops the spare entry so history stays clean.
 */
@Composable
private fun rememberSetupBack(choosingPin: Boolean, onStepBack: () -> Unit, onClose: () -> Unit, kidsStarted: () -> Boolean): () -> Unit {
    val pinNow by rememberUpdatedState(choosingPin)
    val stepBack by rememberUpdatedState(onStepBack)
    val closeNow by rememberUpdatedState(onClose)
    val started by rememberUpdatedState(kidsStarted)
    val st = remember { SetupBackState() }
    DisposableEffect(Unit) {
        // No push: the avatar menu's spare entry is already on top (see KidsHistory), or KIDZONE's after a PIN
        // exit, which this screen now takes over instead of Index.kt.
        KidsHistory.leftSpare = false
        val handler: (org.w3c.dom.events.Event) -> Unit = {
            when {
                st.ignoreNext -> st.ignoreNext = false
                pinNow -> {
                    window.history.pushState(null, "", window.location.href)   // stay here: a fresh entry for the next Back
                    stepBack()
                }
                else -> {
                    st.spare = false   // Back just used it up
                    closeNow()
                }
            }
        }
        window.addEventListener("popstate", handler)
        onDispose {
            window.removeEventListener("popstate", handler)
            if (st.spare) {
                if (started()) KidsHistory.handOff = true else KidsHistory.leftSpare = true
            }
        }
    }
    return {
        if (pinNow) {
            stepBack()
        } else {
            if (st.spare) {
                st.spare = false
                st.ignoreNext = true
                window.history.back()
            }
            closeNow()
        }
    }
}

/**
 * One of the two age cards: the age on top, then a ticked list of what KIDZONE does differently at that age.
 * [look] "young" is bright and sunny, "older" calm. The one not chosen fades back.
 */
@Composable
private fun AgeChoice(title: String, look: String, lines: List<String>, selected: Boolean, dimmed: Boolean, onClick: () -> Unit) {
    Button({
        classes(*listOfNotNull("kids-age", look, if (selected) "on" else null, if (dimmed) "dim" else null).toTypedArray())
        onClick { onClick() }
    }) {
        Div({ classes("t") }) { Text(title) }
        lines.forEach { line -> Div({ classes("l") }) { Span({ classes("tick") }); Span { Text(line) } } }
    }
}

/** Fixed space, plus a share of whatever the screen has left (mobile's Spacer + Spacer(weight)). */
@Composable
private fun SetupGap(px: Int, grow: Double = 1.0) {
    Div({ style { property("min-height", "${px}px"); property("flex", "$grow 1 0") } })
}

/**
 * Avatar menu → KIDZONE. Explains it, asks who it's for (Under 7 / 7 and above), makes the parent create a PIN, then Start. Members only.
 * It never scrolls: on a short screen the whole page shrinks to fit ([watchShrinkToFit], mobile's FitToHeight).
 */
@Composable
fun KidsSetupContent(viewModel: MediaCheckerViewModel, onClose: () -> Unit, onSeePlans: () -> Unit) {
    KidsStyles()
    val kids = viewModel.kids
    val state by kids.state.collectAsState()
    val entitlement by viewModel.entitlement.collectAsState()
    val eligible = remember(entitlement) { kids.isEligible() }
    var choosingPin by remember { mutableStateOf(false) }
    // Mobile reads the icons and the three verdict animations as soon as Kids Mode composes; the web asks for them now (the logo font too).
    LaunchedEffect(Unit) {
        preloadKidsIcons()
        preloadKidsAssets()
    }
    // Browser Back steps out of PIN creation, then closes setup (mobile's PlatformBackHandler); the "‹ Back" link does the same.
    val back = rememberSetupBack(choosingPin, onStepBack = { choosingPin = false }, onClose = onClose,
        kidsStarted = { viewModel.kids.state.value.enabled })

    Div({ classes(*listOfNotNull("kids-root", "noscroll", if (choosingPin) "plain" else null).toTypedArray()) }) {
        Div({ classes("kids-wrap", "setup") }) {
            // The back arrow and the logo share the top row, which saves the page a row's height.
            Div({ classes("kids-setup-top") }) {
                Button({ classes("kids-back"); attr("aria-label", "Back"); onClick { back() } })
                if (!choosingPin) KidzoneTitle(56)
            }
            Div({ classes("kids-shrink") }) {
                Div({
                    classes("kids-shrink-in")
                    ref { inner ->
                        val stop = watchShrinkToFit(inner)
                        onDispose { stop() }
                    }
                }) {
                    if (choosingPin) {
                        // Mobile: 12 under Back before the PIN pad.
                        Div({ style { height(12.px) } })
                        NewPinEntry { pin -> kids.setPin(pin); choosingPin = false }
                    } else {
                        Div({ classes("kids-center") }) {
                            Div({ classes("kids-setup-intro") }) { Text("Hand your phone to your child and let them check titles themselves.") }
                            SetupGap(18)
                            Div({ classes("kids-age-q") }) { Text("How old is your child?") }
                            // One above the other, full width.
                            Div({ classes("kids-ages") }) {
                                AgeChoice(
                                    "Under 7", "young",
                                    listOf("Very simple words, like \"Bad words\". No details.",
                                        "Big pictures, and it reads the answer out loud.",
                                        "Stricter: anything scary is a No."),
                                    selected = state.ageBand == KidsAgeBand.YOUNG, dimmed = state.ageBand == KidsAgeBand.OLDER
                                ) { kids.setAgeBand(KidsAgeBand.YOUNG) }
                                AgeChoice(
                                    "7 and above", "older",
                                    listOf("A sentence or two about each thing to watch for.",
                                        "No spoilers, no details.",
                                        "A Maybe says what to talk to you about. A No suggests safer picks."),
                                    selected = state.ageBand == KidsAgeBand.OLDER, dimmed = state.ageBand == KidsAgeBand.YOUNG
                                ) { kids.setAgeBand(KidsAgeBand.OLDER) }
                            }
                            SetupGap(16)
                            // What's true at both ages, one icon each.
                            Div({ classes("kids-facts") }) {
                                listOf(
                                    "camera" to "Search and scan only",
                                    "lock" to "No settings or payments",
                                    "exit" to "Your PIN to leave",
                                    "kids" to "Saved to your Analyses",
                                ).forEach { (icon, t) -> Div { KidsImg(icon, 32); Span { Text(t) } } }
                            }
                            SetupGap(18)
                            when {
                                !eligible -> {
                                    Div({ classes("kids-setup-member") }) { Text("KIDZONE is included with membership.") }
                                    Button({ classes("kids-btn"); onClick { onSeePlans() } }) { Text("See plans") }
                                }
                                !state.hasPin -> {
                                    Button({ classes("kids-btn"); onClick { choosingPin = true } }) { KidsImg("pin", 44); Text("Create a PIN") }
                                    Div({ classes("kids-setup-hint") }) { Text("You'll set this once. You need it to leave KIDZONE.") }
                                }
                                else -> {
                                    Button({ classes("kids-btn"); if (state.ageBand == null) disabled(); onClick { kids.enter() } }) { KidsImg("rocket", 44); Text("Start KIDZONE") }
                                    if (state.ageBand == null) Div({ classes("kids-setup-hint") }) { Text("Choose your child's age above to start.") }
                                    Button({ classes("kids-link", "up10"); onClick { choosingPin = true } }) { Text("Change PIN") }
                                }
                            }
                            SetupGap(0, grow = 0.5)
                        }
                    }
                }
            }
        }
    }
}

/**
 * Mobile's FitToHeight for the setup page: [inner] is at least as tall as its `.kids-shrink` box (its
 * [SetupGap]s share out what's left); when its content is taller, shrinks it (from the top centre) until it fits. A transform doesn't change [inner]'s layout size, so watching it
 * can't loop. Re-fits when either changes size and once the fonts are in. Returns the cleanup.
 */
private fun watchShrinkToFit(inner: org.w3c.dom.HTMLElement): () -> Unit {
    val box = inner.parentElement as? org.w3c.dom.HTMLElement ?: return {}
    var disposed = false
    val fit: () -> Unit = {
        if (!disposed) {
            val height = inner.offsetHeight.toDouble()
            val room = box.clientHeight.toDouble()
            inner.style.transform = if (height > room && room > 0) "scale(${room / height})" else ""
        }
    }
    fit()
    val fonts: dynamic = document.asDynamic().fonts
    if (fonts != null && fonts != undefined) fonts.ready.then({ fit() })
    val observerClass = window.asDynamic().ResizeObserver
    var observer: dynamic = null
    val resizeHandler: (org.w3c.dom.events.Event) -> Unit = { fit() }
    if (observerClass != null && observerClass != undefined) {
        observer = js("Reflect").construct(observerClass, arrayOf<dynamic>({ _: dynamic -> fit() }))
        observer.observe(inner)
        observer.observe(box)
    } else {
        window.addEventListener("resize", resizeHandler)
    }
    return {
        disposed = true
        if (observer != null) observer.disconnect() else window.removeEventListener("resize", resizeHandler)
    }
}

// ── Styles ──────────────────────────────────────────────────────────────────

@Composable
internal fun KidsStyles() = InjectStyles("kids-mode-styles", """
.kids-root{min-height:100dvh;isolation:isolate;overflow-x:clip;font-family:-apple-system,BlinkMacSystemFont,system-ui,'Segoe UI',Roboto,'Helvetica Neue',Arial,sans-serif;line-height:24px;color:#1E1B4B;-webkit-user-select:none;user-select:none;-webkit-touch-callout:none;-webkit-tap-highlight-color:transparent;touch-action:manipulation}
/* Mobile's plain Text() has Material's fixed 24px line box (text over 22px sets 1.15 below, so wrapped lines never overlap). Buttons don't inherit it by default. */
:where(.kids-root) :is(button,input){font-family:inherit}
:where(.kids-root) button{line-height:inherit}
.kids-root input{-webkit-user-select:text;user-select:text}
.kids-root img{-webkit-user-drag:none}
/* Mobile fades 300ms between screens (Crossfade): the new KIDZONE / setup screen fades in. */
.kids-root:not(.kidsflip-back-face){animation:kids-fade-in .3s cubic-bezier(.4,0,.2,1)}
@keyframes kids-fade-in{from{opacity:0}to{opacity:1}}
/* The gradient is a screen-fixed layer behind the content (iOS ignores background-attachment:fixed), so it stays put while the page scrolls, like mobile's full-screen Box. */
.kids-root::before{content:'';position:fixed;inset:0;z-index:-1;pointer-events:none;background:linear-gradient(180deg,#7FD6FF 0%,#BFF0D8 50%,#FFEFA0 100%)}
/* 8 and over: a calmer sky (mobile's Kidz.CalmBackground). */
.kids-root.older::before,.kidsflip-back-face.older{background:linear-gradient(180deg,#DCE8FA 0%,#F2F5FB 100%)}
.kids-root.plain{background:#fff}
.kids-root.plain::before{display:none}
/* Mobile padding: 20 all round on the pick / check screens; Home is 12 top and 36 bottom (12 + a 24 spacer). */
.kids-wrap{max-width:560px;margin:0 auto;padding:20px;box-sizing:border-box}
.kids-wrap.home{padding:12px 20px 36px}
/* Mobile: 15sp bold in Material's fixed 24px line box, 6px above and below it (36px tall), then a 6px gap. A block (not inline-block) so no line box adds height around it. */
.kids-parents{display:block;background:none;border:none;font-family:inherit;font-size:15px;font-weight:700;line-height:24px;color:#4C4A7A;padding:6px 4px;border-radius:8px;cursor:pointer;margin-bottom:6px}
/* Box the logo and the question are fitted to (watchKidsFit): the probe is a zero-height strip as wide as the box, which the resize observer watches. */
.kids-fit{position:relative}
.kids-fit-probe{position:absolute;left:0;right:0;top:0;height:0;visibility:hidden;pointer-events:none}
@font-face{font-family:'Jumpman';src:url('/fonts/jumpman.ttf') format('truetype');font-display:block}
/* The logo, built to mobile's metrics (KidzoneTitle in KidsComponents.kt). Per-letter variables come from KidzoneTitle(): --adv (advance in em),
   --tilt --bounce --rock --hop --squish --rock-ms --hop-ms --start. Each letter's box is its advance x 1.35 wide (mobile's TextGeometricTransform
   scaleX), boxes overlap by .07em (spacedBy(-0.07 x font size)) and the I sits on top of its neighbours (zIndex). 3.568em wide in all. */
.kidzone-title{display:inline-flex;font-family:'Jumpman','Arial Black',Impact,sans-serif;font-weight:400;line-height:1.1;letter-spacing:0;filter:drop-shadow(0 .07em 0 #000);user-select:none}
.kidzone-title>span{display:flex;justify-content:center;flex:none;width:calc(var(--adv) * 1.35em);animation:kidz-rock var(--rock-ms) cubic-bezier(.4,0,.2,1) var(--start) infinite alternate both,kidz-hop var(--hop-ms) cubic-bezier(0,0,.2,1) calc(var(--start) / 2) infinite alternate both}
.kidzone-title>span+span{margin-left:-.07em}
.kidzone-title>span:nth-child(2){position:relative;z-index:1}
.kidzone-title .g{display:block;-webkit-text-stroke:.11em #000;paint-order:stroke fill;transform-origin:50% 100%;animation:kidz-squish var(--hop-ms) cubic-bezier(0,0,.2,1) calc(var(--start) / 2) infinite alternate both}
/* Rock: tilt +/- rock, FastOutSlowIn, around the letter's middle. Hop: rises by --hop x font size from its --bounce, LinearOutSlowIn. Squish (same timing as the hop, from the feet): scaleY 1 + hop x squish, scaleX 1.35 x (1 - hop x squish x .5). Positive delays + fill both = Compose's StartOffset (holds the first frame). */
@keyframes kidz-rock{from{rotate:calc(var(--tilt) - var(--rock))}to{rotate:calc(var(--tilt) + var(--rock))}}
@keyframes kidz-hop{from{translate:0 calc(var(--bounce) * 1em)}to{translate:0 calc((var(--bounce) - var(--hop)) * 1em)}}
@keyframes kidz-squish{from{scale:1.35 1}to{scale:calc(1.35 * (1 - var(--squish) * .5)) calc(1 + var(--squish))}}
/* Mobile's fillWidth: watchKidsFit sets the font-size so this width (max-content, never squeezed) is exactly the box less 6px a side. 60px until it has run. */
.kidzone-title.fill{display:flex;width:max-content;margin:0 auto;font-size:60px}
/* 8 and over: the letters keep their resting pose. */
.kidzone-title.still>span{animation:none;rotate:var(--tilt);translate:0 calc(var(--bounce) * 1em)}
.kidzone-title.still .g{animation:none;scale:1.35 1}
.kids-img{display:block;object-fit:contain;flex-shrink:0}
.kids-bubble.row{display:flex;align-items:center;gap:12px;text-align:left;padding:11px}  /* 3px border + 11px = mobile's 14dp, measured from the outer edge */
.kids-pill.back{background:#fff;border:none;border-radius:999px;padding:10px 18px;margin-bottom:6px;font-size:18px;font-weight:800;font-family:inherit;color:#8B5CF6;box-shadow:0 2px 4px rgba(0,0,0,.15);cursor:pointer;white-space:nowrap}
.kids-h1{font-size:30px;font-weight:800;line-height:1.15;margin:22px 0 18px}
/* Always one line: watchKidsFit measures it (max-content = its natural width) and picks the biggest size from 30px down to 16px that fits (mobile: auto-size 16-30sp). */
.kids-h1.fit{white-space:nowrap;width:max-content;font-size:30px;margin:24px 0 20px}
.kids-h1.small{font-size:26px;margin:14px 0 10px;text-align:center}
/* Pick screen (mobile): back pill, 16, title, 16, the cards (12 under each), an 8px spacer, "None of these". */
.kids-wrap.pick .kids-pill.back{margin-bottom:16px}
.kids-wrap.pick .kids-h1{margin:0 0 16px}
/* Check screen (mobile): back pill, 12, cover, 18, "Checking...", 10, rocket bar, 6, sub-line, 26, "Play while you wait", 12, the games, 16. */
.kids-wrap.scan .kids-pill.back{display:block;width:fit-content;margin-bottom:12px}
.kids-h1.small.checking{margin:18px 0 10px}
/* Blocked / failed note under its 110px picture: 12 above the words, 28 below (the Try again button follows). */
.kids-h1.small.note{margin:12px 0 28px}
.kids-wait-sub{text-align:center;color:#4C4A7A;font-size:17px;font-weight:600;margin:6px 0 26px}
.kids-label.center.play{margin-bottom:12px}
.kids-title{font-size:26px;font-weight:800;line-height:1.15;margin-top:12px;text-align:center}
/* A long unbroken word (a YouTube-style channel name) wraps mid-word like Compose's Text, instead of overflowing. */
.kids-title,.kids-h1,.kids-verdict .head,.kids-card .x,.kids-choice .t,.kids-choice .d,.kids-row .name,.kids-row .meta{overflow-wrap:anywhere}
.kids-choice .txt{flex:1;min-width:0;margin-left:16px}
.kids-btn{width:100%;min-height:72px;border:none;border-radius:28px;background:linear-gradient(90deg,#FF8A00,#FF3D7F);color:#fff;font-family:inherit;font-size:24px;font-weight:800;line-height:1.15;display:flex;align-items:center;justify-content:center;gap:14px;padding:14px 24px;box-shadow:0 6px 0 #B0124F;cursor:pointer;text-align:left;margin-bottom:6px;transition:transform .06s,box-shadow .06s}
.kids-btn:active:not(:disabled){transform:translateY(6px);box-shadow:0 0 0 #B0124F}
.kids-btn.secondary{background:#fff;color:#1E1B4B;box-shadow:0 6px 0 #B9C3E0}
.kids-btn.purple{background:linear-gradient(90deg,#8B5CF6,#FF3D7F);box-shadow:0 6px 0 #5B21B6}
.kids-btn.retry{background:linear-gradient(90deg,#8B5CF6,#049CD8);box-shadow:0 6px 0 #5B21B6}
.kids-btn:disabled{opacity:.5;cursor:default}
.kids-or{text-align:center;color:#4C4A7A;font-size:17px;font-weight:700;margin:18px 0 10px}
.kids-or.tight{margin:4px 0 18px}
.kids-or.strong{color:#1E1B4B;font-weight:600}
/* The two choice tiles (mobile KidsActionTile): tilted by --tilt and still; the face presses 7px into --edge. */
.kids-tiles{display:flex;gap:30px;padding:8px 6px}
.kids-tile{flex:1;min-width:0;position:relative;border:none;background:none;padding:0 0 7px;font-family:inherit;cursor:pointer;transform-origin:50% 100%;rotate:var(--tilt);-webkit-tap-highlight-color:transparent}
.kids-tile::before{content:'';position:absolute;left:0;right:0;top:7px;bottom:0;border-radius:28px;background:var(--edge)}
.kids-tile .face{position:relative;display:flex;flex-direction:column;align-items:center;gap:10px;padding:18px 10px;border-radius:28px;background:var(--face);color:#fff;font-size:20px;font-weight:800;line-height:24px;text-align:center;transition:transform .06s}
.kids-tile:active:not(:disabled) .face{transform:translateY(7px)}
.kids-tile:disabled{opacity:.5;cursor:default}
/* 7 and under: one camera tile, full width less 24 a side. 8 and over: level tiles, closer together. */
.kids-tiles.single{padding:8px 24px}
.kids-tiles.calm{gap:14px;padding:8px 0}
.kids-typelink{display:block;margin:6px auto 0;background:none;border:none;font-family:inherit;font-size:16px;font-weight:700;line-height:24px;color:#4C4A7A;padding:8px 10px;border-radius:8px;cursor:pointer}
.kids-typelink:disabled{opacity:.5;cursor:default}
/* The name box: folded to nothing until "I'll tell you", then slides down (320ms in, 220ms out; Material's FastOutSlowIn). It opens/closes
   by height + fade; the slide (-40px = half the 80px row, mobile's -it/2) is a one-shot on opening only, closing just folds up.
   The row keeps 10px (sides and bottom) for the Go button's shadow inside the clip; the box gives that back with negative margins. */
.kids-typebox{display:grid;grid-template-rows:0fr;opacity:0;margin:0 -10px;transition:grid-template-rows .22s cubic-bezier(.4,0,.2,1),opacity .16s cubic-bezier(.4,0,.2,1),margin-bottom .22s cubic-bezier(.4,0,.2,1)}
.kids-typebox.open{grid-template-rows:1fr;opacity:1;margin-bottom:-10px;transition:grid-template-rows .32s cubic-bezier(.4,0,.2,1),opacity .26s cubic-bezier(.4,0,.2,1),margin-bottom .32s cubic-bezier(.4,0,.2,1)}
.kids-typebox .inner{min-height:0;overflow:hidden}
.kids-namerow{display:flex;gap:10px;align-items:center;padding:16px 10px 10px}
.kids-typebox.open.slide .kids-namerow{animation:kids-name-in .32s cubic-bezier(.4,0,.2,1)}
@keyframes kids-name-in{from{transform:translateY(-40px)}to{transform:none}}
.kids-namefield{flex:1;min-width:0;position:relative}
.kids-clear{position:absolute;right:6px;top:50%;transform:translateY(-50%);width:40px;height:40px;border:none;border-radius:50%;background:none;font-family:inherit;font-size:18px;font-weight:700;color:#4C4A7A;cursor:pointer}
/* Material's OutlinedTextField: 58px (16 + the 22px text's own 26px line + 16), text 16px in from the outer edge, a 1px purple 45% outline that becomes 2px solid purple on focus (an inset ring, so nothing shifts). */
.kids-input{width:100%;min-width:0;height:58px;border:none;border-radius:24px;padding:0 48px 0 16px;font-size:22px;font-weight:600;color:#1E1B4B;font-family:inherit;background:#fff;outline:none;box-sizing:border-box;box-shadow:inset 0 0 0 1px rgba(139,92,246,.45)}
.kids-input:focus{box-shadow:inset 0 0 0 2px #8B5CF6}
.kids-input::placeholder{color:#9A98C0;font-size:20px;font-weight:400}
.kids-go{width:64px;height:64px;border-radius:50%;border:none;background:linear-gradient(to bottom right,#8B5CF6,#FF3D7F);color:#fff;font-family:inherit;font-size:21px;font-weight:800;cursor:pointer;flex-shrink:0;box-shadow:0 2px 4px rgba(0,0,0,.2),0 4px 8px rgba(0,0,0,.14)}
.kids-go:disabled{color:rgba(255,255,255,.55);cursor:default;box-shadow:none}
.kids-busy{display:flex;align-items:center;justify-content:center;gap:12px;margin-top:18px;font-size:19px;font-weight:700;color:#1E1B4B}
/* Material's indeterminate CircularProgressIndicator: a bare arc (no track) that grows and shrinks (about 1.33s, in the SVG) while it spins.
   Pink 28px by default (.s30 = the My checks cell); .big = the 40px default size, pink, plus .purple (PIN sending) and .white (camera shutter). */
.kids-spin{width:28px;height:28px;flex-shrink:0;background:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 28 28'%3E%3Ccircle cx='14' cy='14' r='12' fill='none' stroke='%23FF3D7F' stroke-width='4' stroke-dasharray='53.5 150.8'%3E%3Canimate attributeName='stroke-dasharray' values='0.9 150.8;53.5 150.8;53.5 150.8' keyTimes='0;.5;1' calcMode='spline' keySplines='.42 0 .58 1;.42 0 .58 1' dur='1.33s' repeatCount='indefinite'/%3E%3Canimate attributeName='stroke-dashoffset' values='0;-21;-74.4' keyTimes='0;.5;1' calcMode='spline' keySplines='.42 0 .58 1;.42 0 .58 1' dur='1.33s' repeatCount='indefinite'/%3E%3C/circle%3E%3C/svg%3E") center/100% 100% no-repeat;animation:kids-spin 1.6s linear infinite}
.kids-spin.s30{width:30px;height:30px}
.kids-spin.big{width:40px;height:40px;background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 40 40'%3E%3Ccircle cx='20' cy='20' r='18' fill='none' stroke='%23FF3D7F' stroke-width='4' stroke-dasharray='80.3 226.2'%3E%3Canimate attributeName='stroke-dasharray' values='1.4 226.2;80.3 226.2;80.3 226.2' keyTimes='0;.5;1' calcMode='spline' keySplines='.42 0 .58 1;.42 0 .58 1' dur='1.33s' repeatCount='indefinite'/%3E%3Canimate attributeName='stroke-dashoffset' values='0;-31.4;-111.6' keyTimes='0;.5;1' calcMode='spline' keySplines='.42 0 .58 1;.42 0 .58 1' dur='1.33s' repeatCount='indefinite'/%3E%3C/circle%3E%3C/svg%3E")}
.kids-spin.big.purple{background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 40 40'%3E%3Ccircle cx='20' cy='20' r='18' fill='none' stroke='%238B5CF6' stroke-width='4' stroke-dasharray='80.3 226.2'%3E%3Canimate attributeName='stroke-dasharray' values='1.4 226.2;80.3 226.2;80.3 226.2' keyTimes='0;.5;1' calcMode='spline' keySplines='.42 0 .58 1;.42 0 .58 1' dur='1.33s' repeatCount='indefinite'/%3E%3Canimate attributeName='stroke-dashoffset' values='0;-31.4;-111.6' keyTimes='0;.5;1' calcMode='spline' keySplines='.42 0 .58 1;.42 0 .58 1' dur='1.33s' repeatCount='indefinite'/%3E%3C/circle%3E%3C/svg%3E")}
.kids-spin.big.white{background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 40 40'%3E%3Ccircle cx='20' cy='20' r='18' fill='none' stroke='%23FFFFFF' stroke-width='4' stroke-dasharray='80.3 226.2'%3E%3Canimate attributeName='stroke-dasharray' values='1.4 226.2;80.3 226.2;80.3 226.2' keyTimes='0;.5;1' calcMode='spline' keySplines='.42 0 .58 1;.42 0 .58 1' dur='1.33s' repeatCount='indefinite'/%3E%3Canimate attributeName='stroke-dashoffset' values='0;-31.4;-111.6' keyTimes='0;.5;1' calcMode='spline' keySplines='.42 0 .58 1;.42 0 .58 1' dur='1.33s' repeatCount='indefinite'/%3E%3C/circle%3E%3C/svg%3E")}
@keyframes kids-spin{to{transform:rotate(360deg)}}
.kids-bubble{background:#fff;border:3px solid #FBD000;border-radius:22px;padding:16px;margin-top:16px;text-align:center;font-size:18px;font-weight:700;color:#1E1B4B;box-shadow:0 2px 4px rgba(0,0,0,.1);cursor:pointer;width:100%;box-sizing:border-box}
.kids-h2{font-size:24px;font-weight:800;line-height:1.15;margin:30px 0 12px}
/* The detail card's flip (rememberKidsFlip): ONE turn, 0 to 180 degrees over 600ms with FastOutSlowIn, and the same turn run backwards on the way back
   (a transition, so a flip reversed half-way turns back from where it is). At rest the card has none of these classes, so no transform at all.
   -s0 / -s180 are the angles it turns from (no transition); -to0 / -to180 are the angles it turns to. perspective 2240px = mobile's cameraDistance
   of 14 (Android's depth unit is 160 dp, so 14 x 160). The back face is pre-turned 180 so that, past the half-way point, it reads the right way round. */
.kidsflip-s0,.kidsflip-to0{transform:perspective(2240px) rotateY(0deg)}
.kidsflip-s180,.kidsflip-to180{transform:perspective(2240px) rotateY(180deg)}
.kidsflip-to0,.kidsflip-to180{transition:transform .6s cubic-bezier(.4,0,.2,1)}
:is(.kidsflip-s0,.kidsflip-s180,.kidsflip-to0,.kidsflip-to180) .kidsflip-back-face{transform:rotateY(180deg)}
/* The header bar stays put; only the body scrolls (mobile: the header Row, then a Column with weight(1f) + verticalScroll). */
.kidsflip-back-face{position:absolute;inset:0;z-index:20;overflow:hidden;display:flex;flex-direction:column;min-height:0;background:linear-gradient(180deg,#7FD6FF 0%,#BFF0D8 50%,#FFEFA0 100%)}
.kidsflip-back-face::before{display:none}
.kidsflip-bar{display:flex;align-items:center;justify-content:space-between;padding:14px 16px 0}
.kidsflip-back{border:none;border-radius:999px;background:#1E1B4B;color:#fff;font-family:inherit;font-weight:800;font-size:15px;padding:9px 16px;cursor:pointer}
.kidsflip-label{font-weight:700;font-size:13px;color:rgba(30,27,75,.7)}
.kidsflip-body{flex:1;min-height:0;overflow-y:auto;padding:14px 20px;display:flex;flex-direction:column;align-items:center;box-sizing:border-box}
/* Loading: 120 above a bare pink 40px arc. Failed: 100 above the line (18 Bold), 14, then a small pink pill (16 ExtraBold, 22 x 10). */
.kidsflip-wait{margin:120px auto 0}
.kidsflip-fail{margin-top:100px;font-size:18px;font-weight:700;line-height:24px;color:#1E1B4B;text-align:center}
.kidsflip-retry{margin-top:14px;border:none;border-radius:999px;background:#FF3D7F;color:#fff;font-family:inherit;font-size:16px;font-weight:800;padding:10px 22px;cursor:pointer}
/* "See what your child saw" in the parent's detail view: 16 in from the card edge (the content sheet is padded 28, so -12), 8 above and below, 10 x 16 padding round a 24px line. */
.kids-preview-btn{display:inline-flex;align-items:center;gap:8px;margin:8px 0 8px -12px;padding:10px 16px;border-radius:999px;background:#1E1B4B;color:#fff;font-size:14px;font-weight:700;line-height:24px;cursor:pointer;user-select:none;transition:filter .08s}
.kids-preview-btn:active{filter:brightness(.8)}
.kids-list{display:flex;flex-direction:column;gap:12px;padding-bottom:12px}
.kids-row{display:flex;align-items:center;gap:14px;width:100%;box-sizing:border-box;background:#fff;border:none;border-radius:20px;padding:10px;box-shadow:0 3px 6px rgba(0,0,0,.15);cursor:pointer;text-align:left;font-family:inherit}
.kids-row .frame{border-radius:14px;padding:4px;flex-shrink:0;line-height:0}
.kids-row .info{flex:1;min-width:0}
.kids-row .name{font-size:18px;font-weight:800;color:#1E1B4B;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden}
.kids-row .meta{font-size:13px;font-weight:600;color:rgba(30,27,75,.6)}
/* Hugs its content (48px sticker / 44px disc or spinner box, as on mobile); the -4px makes the 14px gap before it mobile's 10px. */
.kids-row .end{flex-shrink:0;display:flex;align-items:center;justify-content:center;margin-left:-4px}
/* My checks row, right cell: the white disc (sad / grown-up) or the bare spinner box (checking). */
.kids-disc{width:44px;height:44px;border-radius:50%;background:#fff;display:flex;align-items:center;justify-content:center;box-shadow:0 2px 4px rgba(0,0,0,.2)}
.kids-spinbox{width:44px;height:44px;display:flex;align-items:center;justify-content:center}
/* A cover (KidsCover): the tile is tinted with the media type's colour (inline, with its size and corner radius); .cv is the picture (hidden until it has loaded, then it covers the tile), .spin the loading spinner, .glyph the emoji shown if the picture fails. */
.kids-cover{position:relative;display:flex;align-items:center;justify-content:center;flex-shrink:0;overflow:hidden;box-sizing:border-box}
.kids-cover>.cv{position:absolute;left:0;top:0;width:100%;height:100%;object-fit:cover;display:block;opacity:0}
.kids-cover>.cv.on{opacity:1}
.kids-cover>.spin{flex:none;background-repeat:no-repeat;background-size:100% 100%;animation:kids-spin 1.6s linear infinite}
.kids-cover>.glyph{line-height:1}
.kids-cover-frame{background:#fff;border-radius:20px;padding:6px;box-shadow:0 4px 10px rgba(0,0,0,.15)}
.kids-cover-frame.result{border-radius:18px;padding:5px}
/* The verdict sign (mobile VerdictSign). font-size = the disc size, so every part scales in em. */
.kids-sign{position:relative;border-radius:50%;background:#fff;box-shadow:0 3px 8px rgba(0,0,0,.2);display:flex;align-items:center;justify-content:center;flex-shrink:0}
.kids-sign .light{width:.368em;height:.8em;border-radius:.118em;background:#2B2D42;display:flex;flex-direction:column;align-items:center;justify-content:space-around;padding:.03em 0;box-sizing:border-box}
.kids-sign .light span{width:.22em;height:.22em;border-radius:50%;opacity:.22}
.kids-sign .light .red{background:#FF3B30}.kids-sign .light .amber{background:#FFB300}.kids-sign .light .green{background:#22C55E}
.kids-sign .light span.on{opacity:1;box-shadow:0 0 0 .05em rgba(255,255,255,.12),0 0 .09em .05em currentColor}
.kids-sign .light .red.on{color:rgba(255,59,48,.45)}.kids-sign .light .amber.on{color:rgba(255,179,0,.45)}.kids-sign .light .green.on{color:rgba(34,197,94,.45)}
.kids-center{display:flex;flex-direction:column;align-items:center;text-align:center;width:100%}
/* Mobile's choice card: a 10px accent strip the full height of the card (the card's rounded corners clip it), then 14px of padding; at least 118px tall. 16px from the picture to the words, none from the words to the arrow. */
.kids-choice{width:100%;box-sizing:border-box;min-height:118px;display:flex;align-items:center;background:linear-gradient(90deg,var(--accent,#049CD8) 10px,#fff 10px);border:none;border-radius:24px;padding:14px 14px 14px 24px;margin-bottom:12px;box-shadow:0 3px 8px rgba(0,0,0,.1);cursor:pointer;text-align:left;font-family:inherit}
.kids-choice-icon{width:64px;display:flex;align-items:center;justify-content:center;flex-shrink:0}
.kids-choice .t{font-size:20px;font-weight:800;color:#1E1B4B;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden}
.kids-choice .d{font-size:15px;font-weight:600;color:#4C4A7A}
.kids-choice .chev{background:var(--accent,#049CD8);width:40px;height:40px;border-radius:50%;color:#fff;font-size:28px;font-weight:900;display:flex;align-items:center;justify-content:center;flex-shrink:0;line-height:1}
.kids-rocketbar{position:relative;width:100%;height:52px;margin:0}
.kids-rocketbar .track{position:absolute;left:0;right:0;top:14px;height:24px;background:#fff;border-radius:12px;overflow:hidden;box-shadow:0 2px 4px rgba(30,27,75,.18)}
.kids-rocketbar .fill{height:100%;border-radius:12px;transition:width .8s cubic-bezier(.4,0,.2,1);background-color:#FF8A00;background-image:linear-gradient(180deg,rgba(255,255,255,.28) 0 32%,transparent 32% 80%,rgba(194,94,0,.55) 80%),repeating-linear-gradient(135deg,#FFB547 0 15.12px,transparent 15.12px 23.76px);background-size:100% 100%,33.6px 33.6px;animation:kids-stripes .9s linear infinite}
/* Mobile's stripes: '/' lines 1.4 x the bar's height apart (33.6px), 15.12px thick (the 135deg gradient's period is 33.6 / sqrt 2 = 23.76), sliding right one period every 0.9s. */
@keyframes kids-stripes{from{background-position:0 0,0 0}to{background-position:0 0,33.6px 0}}
.kids-rocketbar .rocket{position:absolute;top:3px;width:46px;height:46px;transition:left .8s cubic-bezier(.4,0,.2,1);animation:kids-bob .62s cubic-bezier(.4,0,.2,1) infinite alternate}
@keyframes kids-bob{from{transform:translateY(-2.5px) rotate(45deg)}to{transform:translateY(2.5px) rotate(45deg)}}
.kids-reading{position:fixed;inset:0;z-index:1500;background:linear-gradient(180deg,#7FD6FF 0%,#BFF0D8 50%,#FFEFA0 100%);display:flex;flex-direction:column;align-items:center;justify-content:center}
.kids-reading>.kids-pill{position:absolute;top:calc(20px + env(safe-area-inset-top));left:20px}
.kids-reading .stage{position:relative;width:320px;height:360px;display:flex;align-items:center;justify-content:center}
.kids-reading .snap{background:#fff;padding:10px;border-radius:18px;box-shadow:0 10px 24px rgba(30,27,75,.28);animation:kids-snap 1.3s cubic-bezier(.37,0,.63,1) -.65s infinite alternate}
/* Mobile: rotation = -5 + 3 sin(loop) over 2.6s, i.e. a sine swing between -8 and -2 degrees that starts at -5 heading for -2. Half a period each way, eased like a sine; the negative delay starts it at the middle. */
@keyframes kids-snap{from{transform:rotate(-8deg)}to{transform:rotate(-2deg)}}
.kids-reading .pic{position:relative;width:210px;height:270px;border-radius:12px;overflow:hidden;background:#E9E6FF;display:flex;align-items:center;justify-content:center}
.kids-reading .pic>img:not(.kids-img){width:100%;height:100%;object-fit:cover}
.kids-reading .beam{position:absolute;left:0;right:0;top:0;height:54px;background:linear-gradient(180deg,transparent,rgba(251,208,0,.65),transparent);animation:kids-beam 1.3s linear infinite alternate}
@keyframes kids-beam{from{transform:translateY(0)}to{transform:translateY(216px)}}
/* Mobile's sparkle: a 34px four-pointed star (inner points at 28% of the radius = 36% / 64%), scale and alpha = max(0, sin(2 loop + phase)): up and down once in the first half of every 1.3s, then gone for the second half. The phase (0, 1.6, 3.1, 4.5, 2.3 rad = 0, 331, 641, 931, 476 ms into the cycle) is a negative delay. Positions are mobile's offsets from the middle of the 320 x 360 stage. */
.kids-reading .spark{position:absolute;width:34px;height:34px;clip-path:polygon(50% 0,64% 36%,100% 50%,64% 64%,50% 100%,36% 64%,0 50%,36% 36%);animation:kids-spark 1.3s linear infinite;opacity:0;transform:scale(0)}
.kids-reading .s1{left:3px;top:13px}.kids-reading .s2{left:281px;top:51px;animation-delay:-.331s}.kids-reading .s3{left:293px;top:283px;animation-delay:-.641s}.kids-reading .s4{left:-7px;top:303px;animation-delay:-.931s}.kids-reading .s5{left:137px;top:-7px;animation-delay:-.476s}
@keyframes kids-spark{0%{transform:scale(0);opacity:0;animation-timing-function:cubic-bezier(.61,1,.88,1)}25%{transform:scale(1);opacity:1;animation-timing-function:cubic-bezier(.12,0,.39,0)}50%,100%{transform:scale(0);opacity:0}}
.kids-reading .glass{position:absolute;left:50%;top:50%;width:120px;height:120px;margin:-60px 0 0 -60px;background:${GLASS_BACKGROUND} center/100% 100% no-repeat;animation:kids-glass 2.6s linear infinite}
@keyframes kids-glass{${GLASS_KEYFRAMES}}
.kids-reading .line{margin-top:18px;font-size:28px;font-weight:900;line-height:1.15;color:#1E1B4B;white-space:nowrap}
.kids-reading .line span{display:inline-block;animation:kids-hop 1.4s linear infinite}
/* Mobile: hop = max(0, sin(wave)) x 9px over 1.4s, so a sine-shaped hop for 700ms (up on easeOutSine, down on easeInSine) and 700ms at rest; each letter is 0.45 rad (about 100ms) behind the one before. */
@keyframes kids-hop{0%{transform:translateY(0);animation-timing-function:cubic-bezier(.61,1,.88,1)}25%{transform:translateY(-9px);animation-timing-function:cubic-bezier(.12,0,.39,0)}50%,100%{transform:translateY(0)}}
.kids-cam{position:fixed;inset:0;z-index:2000;background:#000;overflow:hidden}
.kids-cam video{position:absolute;inset:0;width:100%;height:100%;object-fit:cover}
.kids-cam .box{position:absolute;left:50%;top:calc(50% - 24px);transform:translate(-50%,-50%);width:min(78vw,420px);aspect-ratio:1/1.3;max-height:56vh;border:6px solid #FBD000;border-radius:28px;box-shadow:0 0 0 9999px rgba(0,0,0,.5);animation:kids-glow .9s ease-in-out infinite alternate}
@keyframes kids-glow{from{border-color:rgba(251,208,0,.55)}to{border-color:#FBD000}}
.kids-cam .hint{position:absolute;bottom:calc(100% + 18px);left:50%;transform:translateX(-50%);white-space:nowrap;background:#fff;color:#1E1B4B;font-size:20px;font-weight:800;padding:10px 20px;border-radius:999px}
.kids-cam .close{position:absolute;top:calc(16px + env(safe-area-inset-top));left:16px;width:52px;height:52px;border-radius:50%;border:none;background:#fff;color:#1E1B4B;font-size:22px;font-weight:900;box-shadow:0 3px 8px rgba(0,0,0,.3);cursor:pointer}
.kids-cam .shutter{position:absolute;left:50%;bottom:calc(36px + env(safe-area-inset-bottom));width:96px;height:96px;margin-left:-48px;border-radius:50%;border:7px solid #fff;background:linear-gradient(135deg,#FF8A00,#FF3D7F);box-shadow:0 6px 16px rgba(0,0,0,.35);display:flex;align-items:center;justify-content:center;cursor:pointer}
.kids-cam .shutter:disabled{animation:none;cursor:default}
.kids-cam .flash{position:absolute;inset:0;background:#fff;animation:kids-flash .35s ease-out forwards}
@keyframes kids-flash{from{opacity:.9}to{opacity:0}}
.kids-games{display:grid;grid-template-columns:repeat(2,1fr);gap:14px;width:100%;margin:0 0 16px}
.kids-game{border:none;border-radius:22px;padding:0;background:#fff;box-shadow:0 4px 10px rgba(0,0,0,.14);cursor:pointer;font-family:inherit;overflow:hidden;text-align:left}
.kids-game .pic{display:block;width:100%;aspect-ratio:1.6;object-fit:cover}
.kids-game .row{display:flex;align-items:center;padding:10px 12px}
.kids-game .name{flex:1;font-size:18px;font-weight:800;color:#1E1B4B}
.kids-game .play{width:32px;height:32px;border-radius:50%;color:#fff;font-size:14px;font-weight:900;display:flex;align-items:center;justify-content:center}
.kids-game-box{width:100%;margin:0 0 14px;background:#fff;border-radius:24px;overflow:hidden}
.kids-game-modal{position:fixed;inset:0;z-index:1200;background:rgba(30,27,75,.3);display:flex;align-items:center;justify-content:center;padding:calc(16px + env(safe-area-inset-top)) 16px calc(16px + env(safe-area-inset-bottom));box-sizing:border-box}
.kids-game-modal .card{width:100%;max-width:560px;max-height:100%;overflow-y:auto;background:#fff;border-radius:28px;box-shadow:0 14px 40px rgba(0,0,0,.35)}
/* Islamic Trivia: the wide card under Runner and Flappy, and the game itself (mobile's IslamicTriviaGame). */
.kids-game.wide{grid-column:1/-1}
.kids-game .trivia-art{display:flex;align-items:center;justify-content:center;gap:10px;width:100%;aspect-ratio:3.2;background:linear-gradient(to bottom right,#14B8A6,#0E7490)}
.kids-game .trivia-art .disc{width:54px;height:54px;border-radius:50%;background:#fff;color:#0D9488;font-size:36px;font-weight:900;display:flex;align-items:center;justify-content:center;line-height:1}
.kids-game .trivia-art .disc.ex{color:#FF8A00}
.kids-game .trivia-art .moon{width:38px;height:38px;border-radius:50%;box-shadow:-9px 4px 0 0 #FBD000;transform:translateX(5px)}
.kids-trivia{background:#F3F7FF;padding:14px;display:flex;flex-direction:column;align-items:center;text-align:center;color:#1E1B4B}
.kids-trivia .h{font-size:24px;font-weight:800;line-height:30px}
.kids-trivia .h.big{font-size:26px;line-height:32px}
.kids-trivia .sub{font-size:16px;font-weight:600;line-height:22px;color:#4C4A7A;margin:4px 0 14px}
.kids-trivia .chunky{width:100%;display:flex;align-items:center;border:none;border-radius:18px;padding:12px 18px;margin-bottom:10px;font-family:inherit;font-size:22px;font-weight:800;color:#fff;cursor:pointer;text-align:left}
.kids-trivia .chunky.center{justify-content:center;text-align:center;font-size:20px}
.kids-trivia .chunky .grow{flex:1}
.kids-trivia .chunky.easy{background:#22C55E;box-shadow:0 4px 0 #15803D}
.kids-trivia .chunky.medium{background:#FFB300;box-shadow:0 4px 0 #B45309}
.kids-trivia .chunky.hard{background:#FF3B30;box-shadow:0 4px 0 #B91C1C}
.kids-trivia .chunky.next{background:#8B5CF6;box-shadow:0 4px 0 #5B21B6}
.kids-trivia .top{width:100%;display:flex;align-items:center;justify-content:space-between}
.kids-trivia .count{font-size:15px;font-weight:800;color:#4C4A7A}
.kids-trivia .level{font-size:14px;font-weight:800;color:#fff;background:#0D9488;border-radius:999px;padding:3px 10px}
.kids-trivia .bar{width:100%;height:10px;border-radius:999px;background:#fff;margin:8px 0 14px;overflow:hidden}
.kids-trivia .bar>div{height:100%;border-radius:999px;background:#0D9488}
.kids-trivia .q{font-size:21px;font-weight:800;line-height:27px;margin-bottom:14px}
.kids-trivia .answer{width:100%;min-height:52px;display:flex;align-items:center;gap:8px;border:2px solid #DCE3F2;border-radius:16px;background:#fff;padding:10px 14px;margin-bottom:8px;font-family:inherit;font-size:18px;font-weight:700;color:#1E1B4B;text-align:left;cursor:pointer;box-sizing:border-box}
.kids-trivia .answer:disabled{cursor:default}
.kids-trivia .answer .grow{flex:1}
.kids-trivia .answer.right{background:#22C55E;border-color:#22C55E;color:#fff}
.kids-trivia .answer.wrong{background:#FF3B30;border-color:#FF3B30;color:#fff}
.kids-trivia .answer .mark{font-size:20px;font-weight:900}
.kids-trivia .fact{width:100%;box-sizing:border-box;background:#fff;border-radius:16px;padding:12px;margin:4px 0 12px;text-align:left;font-size:16px;font-weight:500;line-height:22px}
.kids-trivia .fact .verdict{font-size:18px;font-weight:800;margin-bottom:2px}
.kids-trivia .fact .verdict.right{color:#15803D}
.kids-trivia .fact .verdict.wrong{color:#FF3B30}
.kids-game-modal .ready{display:flex;align-items:center;justify-content:center;gap:10px;background:#FBD000;padding:14px;font-size:17px;font-weight:800;color:#1E1B4B;cursor:pointer}
.kids-game-modal .head{display:flex;align-items:center;padding:10px 10px 0 18px}
.kids-game-modal .name{flex:1;font-size:22px;font-weight:800;color:#1E1B4B}
.kids-game-modal .x{width:48px;height:48px;border-radius:50%;border:none;background:#F1EFFA;color:#1E1B4B;font-size:20px;font-weight:900;cursor:pointer}
.kids-game-modal .kids-game-box{margin:10px;width:auto;border-radius:20px}
.kids-verdict{width:100%;border-radius:28px;padding:12px 14px;box-sizing:border-box;margin:16px 0 30px;text-align:left;display:flex;flex-direction:row;align-items:center;gap:16px}
.kids-verdict .w{flex:1;min-width:0;font-size:36px;font-weight:900;line-height:36px;color:#fff;text-shadow:0 5px 0 var(--edge)}
.kids-verdict.older .w{font-size:32px}
.kids-verdict .head{font-size:32px;font-weight:900;line-height:1.15;color:#fff}
/* Mobile: a tip card is 22 radius with 16 all round, text at the top; a "What's inside" card is 20 radius with 14 all round, text centred. Body text sits in a 25px (tip) / 24px (point) line box. */
.kids-card{width:100%;box-sizing:border-box;border-radius:22px;padding:16px;display:flex;gap:12px;align-items:flex-start;margin-bottom:10px;text-align:left;background:#fff}
.kids-card .e{width:52px;height:52px;border-radius:50%;background:#fff;display:flex;align-items:center;justify-content:center;font-size:26px;line-height:1.15;flex-shrink:0}
.kids-card .l{font-size:15px;font-weight:800}
.kids-card .x{font-size:19px;font-weight:600;line-height:25px;color:#1E1B4B}
.kids-card.point{align-items:center;border-radius:20px;padding:14px}
.kids-card.point .x{font-size:20px;font-weight:800;line-height:24px}
/* 8 and over: white point cards with a sentence under the label. */
.kids-root.older .kids-card.point{box-shadow:0 1px 3px rgba(30,27,75,.12)}
.kids-root.older .kids-card.point .e{width:44px;height:44px;background:#EEF2FA}
.kids-root.older .kids-card.point .x{font-size:18px}
.kids-card.point.detail{align-items:flex-start}
.kids-card.point .d{font-size:16px;font-weight:500;line-height:22px;color:#4C4A7A;margin-top:2px}
/* Titles suggested under the name box (mobile's KidsSuggestions): a white card of rows split by hairlines. */
.kids-suggest{width:100%;box-sizing:border-box;margin-top:10px;background:#fff;border-radius:22px;box-shadow:0 3px 8px rgba(0,0,0,.14);padding:6px 0;display:flex;flex-direction:column}
.kids-suggest .row{display:flex;align-items:center;gap:12px;width:100%;border:none;background:none;padding:10px 14px;font-family:inherit;text-align:left;cursor:pointer;position:relative}
.kids-suggest .row+.row::before{content:"";position:absolute;top:0;left:16px;right:16px;height:1px;background:#EDEBF7}
.kids-suggest .txt{flex:1;min-width:0}
.kids-suggest .t{font-size:18px;font-weight:800;line-height:23px;color:#1E1B4B;overflow:hidden;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical}
.kids-suggest .d{font-size:14px;font-weight:600;line-height:19px;color:#4C4A7A;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.kids-suggest .chev{font-size:26px;font-weight:900;color:#8B5CF6;line-height:1}
.kids-suggest .row:active{filter:brightness(.95)}
/* "Talk to a grown up": a bold purple block on a darker 5px edge, like the verdict. */
.kids-talk{position:relative;overflow:hidden;width:100%;min-height:96px;box-sizing:border-box;border-radius:24px;padding:18px;display:flex;align-items:center;margin-bottom:15px;text-align:left;background:linear-gradient(to bottom right,#8B5CF6,#7C3AED);box-shadow:0 5px 0 #5B21B6}
.kids-talk .x{font-size:20px;font-weight:800;line-height:26px;color:#fff}
/* The bubbles over the talk card (mobile's BubbleCover). */
.kids-bubbles{position:absolute;inset:0;cursor:pointer;user-select:none;-webkit-user-select:none;touch-action:manipulation;-webkit-tap-highlight-color:transparent}
.kids-bubbles .b{position:absolute;border-radius:50%;box-sizing:border-box;border:2px solid rgba(255,255,255,.7);background:radial-gradient(circle at 31% 29%,rgba(255,255,255,.8) 0 10%,transparent 11%),var(--c);opacity:.97;transition:transform .24s ease-out,opacity .24s ease-out;pointer-events:none}
.kids-bubbles .b.pop{transform:scale(1.45);opacity:0}
.kids-bubbles .lbl{position:absolute;left:50%;top:50%;transform:translate(-50%,-50%);background:#fff;border-radius:999px;padding:8px 18px;font-size:18px;font-weight:800;line-height:24px;color:#5B21B6;box-shadow:0 3px 8px rgba(0,0,0,.2);white-space:nowrap;pointer-events:none}
/* 8 and over: "Positive aspects", ticked lines on the Good stuff yellow. */
.kids-label.positives{margin-top:6px}
.kids-positives{width:100%;box-sizing:border-box;background:#FFF1B8;border-radius:22px;padding:16px;margin-bottom:10px;display:flex;flex-direction:column;gap:10px;text-align:left}
.kids-positives .l{display:flex;align-items:flex-start;gap:10px;font-size:17px;font-weight:600;line-height:23px;color:#1E1B4B}
.kids-positives .tick{flex:none;width:20px;height:20px;margin-top:2px;border-radius:50%;background:#22C55E url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 15 15'%3E%3Cpath d='M4.05 7.8 6.6 10.2 11.1 5.25' fill='none' stroke='%23fff' stroke-width='2.1' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E") center/100% no-repeat}
.kids-btn.speak{margin-top:-15px;margin-bottom:24px}
.kids-ai-voice{margin:-18px 0 22px;text-align:center;font-size:13px;font-weight:600;color:#4C4A7A}
.kids-emoji{font-size:32px;line-height:1}
.kids-label.alts{margin-top:14px}
.kids-alts{display:grid;grid-template-columns:repeat(4,1fr);gap:10px;width:100%}
.kids-alt{border:none;background:none;padding:0 0 6px;font-family:inherit;cursor:pointer;display:flex;flex-direction:column;align-items:center;border-radius:16px;min-width:0}
.kids-cover-frame.alt{border-radius:12px;padding:3px;box-shadow:0 3px 6px rgba(0,0,0,.15)}
.kids-alt .n{margin-top:6px;font-size:13px;font-weight:700;line-height:16px;color:#1E1B4B;text-align:center;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden;overflow-wrap:anywhere}
.kids-age-q{font-size:19px;font-weight:800;line-height:24px;color:#1E1B4B;margin-bottom:10px}
.kids-ages{display:flex;flex-direction:column;gap:12px;width:100%}
/* Age cards (mobile's AgeChoice): 7 and under bright and sunny, 8 and above calm. The chosen one gets a 3px edge (padding drops to match), the other fades back. */
.kids-age{border:1px solid #fff;border-radius:22px;padding:12px 14px;text-align:left;font-family:inherit;cursor:pointer;box-shadow:0 2px 4px rgba(0,0,0,.12);display:flex;flex-direction:column;justify-content:flex-start;box-sizing:border-box}
.kids-age.young{background:linear-gradient(180deg,#FFF1A6,#FFC56B)}
.kids-age.older{background:linear-gradient(180deg,#EEF3FD,#CCDAF5)}
.kids-age.on{border-width:3px;padding:10px 12px;box-shadow:0 6px 12px rgba(0,0,0,.18)}
.kids-age.young.on{border-color:#F76707}
.kids-age.older.on{border-color:#3B5BDB}
.kids-age.dim{opacity:.6}
.kids-age .t{font-size:18px;font-weight:800;line-height:24px;color:#1E1B4B;white-space:nowrap}
.kids-age .l{display:flex;align-items:flex-start;gap:8px;font-size:13px;font-weight:600;line-height:17px;color:#1E1B4B;margin-top:7px}
/* A white tick on a small disc in the card's colour (mobile's CheckDot). */
.kids-age .tick{flex:none;width:15px;height:15px;margin-top:1px;border-radius:50%;background:var(--tick) url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 15 15'%3E%3Cpath d='M4.05 7.8 6.6 10.2 11.1 5.25' fill='none' stroke='%23fff' stroke-width='2.1' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E") center/100% no-repeat}
.kids-age.young{--tick:#F76707}
.kids-age.older{--tick:#3B5BDB}
.kids-facts{display:grid;grid-template-columns:repeat(4,1fr);gap:8px;width:100%}
.kids-facts>div{display:flex;flex-direction:column;align-items:center;gap:4px;font-size:12px;font-weight:700;line-height:15px;color:#1E1B4B;text-align:center}
.kids-label{width:100%;text-align:left;font-size:22px;font-weight:800;margin-bottom:10px}
.kids-label.center{text-align:center}
.kids-scrim{position:fixed;inset:0;background:rgba(30,27,75,.55);display:flex;align-items:center;justify-content:center;padding:24px;z-index:3000;box-sizing:border-box}
.kids-modal{background:#fff;border-radius:32px;max-width:420px;width:100%;box-sizing:border-box;overflow:hidden}
.kids-modal-head{background:linear-gradient(90deg,#8B5CF6,#FF3D7F);display:flex;justify-content:center;padding:18px 0}
/* Mobile: 24 all round; the title (28 Black), 8, the line (19 SemiBold), then for testers a 10 gap and a 14 note, and 20 above the OK button. */
.kids-modal-body{padding:24px;box-sizing:border-box}
.kids-modal-title{font-size:28px;font-weight:900;line-height:1.15;color:#1E1B4B;margin:0 0 8px}
.kids-modal-msg{font-size:19px;font-weight:600;color:#4C4A7A;margin:0}
.kids-modal-note{font-size:14px;color:#B91C1C;margin:10px 0 0}
.kids-modal-body .kids-btn{margin-top:20px}
.kids-sheet{position:fixed;inset:0;background:#fff;z-index:3001;overflow-y:auto}
/* Mobile's exit sheet and setup screen are padded 24 all round (the pick / check screens are 20). */
.kids-sheet .kids-wrap{padding:24px}
/* The setup page never scrolls (mobile's FitToHeight): one screen tall, and the part under Back shrinks to fit when it must. */
.kids-root.noscroll{height:100dvh;min-height:0;overflow:hidden}
.kids-wrap.setup{padding:16px 24px;height:100%;display:flex;flex-direction:column}
.kids-shrink{flex:1;min-height:0}
.kids-shrink-in{transform-origin:top center;min-height:100%;display:flex;flex-direction:column}
.kids-shrink-in>.kids-center{flex:1}
.kids-setup-top{position:relative;display:flex;justify-content:center;align-items:center;min-height:48px}
/* Mobile's IconButton with Icons.AutoMirrored.Filled.ArrowBack (28px, purple). */
.kids-back{position:absolute;left:0;top:50%;translate:0 -50%;width:48px;height:48px;border:none;border-radius:50%;cursor:pointer;background:transparent url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath fill='%238B5CF6' d='M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z'/%3E%3C/svg%3E") center/28px no-repeat}
.kids-back:active{background-color:rgba(139,92,246,.12)}
/* The PIN pad's words (mobile): lock, 8, title 24 ExtraBold, 4, subtitle 15 in NavySoft, 20, dots, 10, the error line (24 tall), 12, the keys. The title is over 22px, so it
   gets the 1.15 line box rather than mobile's cramped 24px; its margins give back the 3.6px that adds (1.8 either side) so everything around it lands where mobile's does. */
.kids-pin-title{font-size:24px;font-weight:800;line-height:1.15;margin:6.2px 0 -1.8px;text-align:center}
.kids-pin-sub{font-size:15px;font-weight:400;line-height:24px;color:#4C4A7A;margin:4px 0 0;text-align:center}
/* The hint under a title with nothing between them (Check your email, We couldn't send the code): 16 normal. */
.kids-pin-sub.hint{font-size:16px;margin-top:0}
/* Sending a code: 40 above a purple 40px arc, 16, then the line (18, Navy, normal weight), stacked. */
.kids-pin-wait{margin-top:40px}
.kids-pin-send{margin-top:16px;font-size:18px;line-height:24px;color:#1E1B4B;text-align:center}
/* We couldn't send the code: 30 above the line (22 Bold). */
.kids-pin-fail{margin-top:30px;font-size:22px;font-weight:700;line-height:24px;text-align:center}
.kids-forgot{margin-top:12px}
.kids-pin-dots{display:flex;gap:18px;justify-content:center;margin:20px 0 0}
.kids-pin-dots span{width:20px;height:20px;border-radius:50%;border:2px solid #8B5CF6;box-sizing:border-box}
.kids-pin-dots span.on{background:#8B5CF6}
.kids-pin-dots.err span{border-color:#EF4444}
/* Mobile's shake: 18, -16, 12, -8, 4, 0 px, each a 45ms tween (FastOutSlowIn) = 270ms. The easing applies between each pair of keyframes. */
.kids-pin-dots.shake{animation:kids-shake .27s cubic-bezier(.4,0,.2,1)}
@keyframes kids-shake{0%{transform:translateX(0)}16.667%{transform:translateX(18px)}33.333%{transform:translateX(-16px)}50%{transform:translateX(12px)}66.667%{transform:translateX(-8px)}83.333%{transform:translateX(4px)}100%{transform:translateX(0)}}
.kids-pin-err{color:#EF4444;font-size:14px;line-height:24px;min-height:24px;margin-top:10px;text-align:center}
/* The keypad rows are padded 7 above and below (14 between them), 12 under the error line. */
.kids-keys{display:grid;grid-template-columns:repeat(3,72px);gap:14px 20px;justify-content:center;margin-top:12px;padding:7px 0}
.kids-key{width:72px;height:72px;border-radius:50%;border:none;background:#EFEDFB;font-family:inherit;font-size:28px;font-weight:700;line-height:1.15;color:#1E1B4B;cursor:pointer}
.kids-key.blank{background:transparent;cursor:default}
/* Material's TextButton: 12 across, 8 above and below a 24px line (40 tall at least, 64 wide), purple Bold; 16px unless a size is given. A block, so no line box adds height around it. */
.kids-link{display:block;background:none;border:none;color:#8B5CF6;font-family:inherit;font-size:16px;font-weight:700;line-height:24px;cursor:pointer;padding:8px 12px;min-width:64px;min-height:40px}
.kids-link.s17{font-size:17px}
.kids-link.s14{font-size:14px}
.kids-link.up8{margin-top:8px}
.kids-link.up10{margin-top:10px}
/* The code box (mobile's OutlinedTextField): Navy text, a 1px #E5E5E5 outline that becomes 2px teal on focus (the second pixel is an inset ring, so nothing shifts). */
.kids-code{width:240px;height:64px;font-size:28px;letter-spacing:6px;text-align:center;color:#1E1B4B;border:1px solid #E5E5E5;border-radius:20px;font-family:inherit;box-sizing:border-box;outline:none;background:#fff}
.kids-code:focus{border-color:#0E7C7B;box-shadow:inset 0 0 0 1px #0E7C7B}
.kids-code-err{margin-top:8px;font-size:14px;line-height:24px;color:#EF4444;text-align:center}
/* Setup (mobile): the intro 14 under the logo and 22 above the card; "included with membership" 17 Bold with 14 before See plans; the set-once note 14 SemiBold in NavySoft, 10 under the button. */
.kids-setup-intro{font-size:17px;font-weight:600;line-height:24px;color:#1E1B4B;text-align:center;margin-top:8px}
.kids-setup-member{font-size:17px;font-weight:700;line-height:24px;color:#1E1B4B;text-align:center;margin-bottom:14px}
.kids-setup-hint{font-size:14px;font-weight:600;line-height:24px;color:#4C4A7A;text-align:center;margin-top:10px}
/* Every full-screen layer swallows scrolling (mobile's layers swallow input): nothing behind it scrolls or chains. Stacking, bottom to top: game 1200, reading 1500, camera 2000, Ask a grown-up 3000, PIN sheet 3001. */
.kids-reading,.kids-cam,.kids-scrim,.kids-game-modal,.kids-sheet{overscroll-behavior:contain}
.kids-reading,.kids-cam,.kids-game-modal{overflow:hidden}
.kids-scrim{overflow-y:auto}
.kids-reading,.kids-cam{touch-action:none}
/* Press feedback for everything tappable (mobile's ripple). The tiles and .kids-btn sink into their edge instead. */
.kids-parents,.kids-row,.kids-bubble,.kids-go,.kids-clear,.kids-link,.kids-key,.kids-pill,.kids-choice,.kids-game,.kids-alt,.kids-age,.kids-typelink,.kidsflip-back,.kidsflip-retry,.kids-game-modal .ready,.kids-game-modal .x,.kids-cam .close,.kids-cam .shutter{transition:filter .08s,transform .08s}
.kids-parents:active,.kids-row:active,.kids-bubble:active,.kids-clear:active,.kids-link:active,.kids-key:not(.blank):not(:disabled):active,.kidsflip-back:active,.kidsflip-retry:active,.kids-game-modal .ready:active,.kids-game-modal .x:active,.kids-cam .close:active,.kids-cam .shutter:active:not(:disabled){filter:brightness(.93)}
.kids-go:active:not(:disabled){filter:brightness(.92)}
.kids-choice:active,.kids-game:active,.kids-pill:active,.kids-alt:active,.kids-age:active{transform:scale(.97);filter:brightness(.95)}
""")
